/**
 * Elite Batch 12 Part 3 — Workflow Templates 1088–1107
 * SEO technical workflows: schema opportunities, meta gaps, internal linking,
 * stale content, topical clusters, comparison/alternatives pages, glossaries,
 * programmatic SEO, FAQ clusters, local SEO, query-to-brief, bibliographies,
 * FAQ/HowTo schema drafters, author EEAT, article update scoring, SEO digests,
 * support-to-SEO pipelines, and changelog-to-launch posts.
 *
 * DESIGN RULES (inherited from prebuiltWorkflows.ts):
 * 1. Every variable MUST come from a real API response field or AI-generated output
 * 2. No magic variables — if a condition checks something, an upstream node must produce it
 * 3. Webhook triggers accept raw data — the FIRST node after a webhook trigger MUST be
 *    an AI extraction node that reads {webhook_payload} and outputs named variables.
 *    NEVER assume webhook payloads have specific field names (no {email}, {name}, etc.)
 * 4. Every condition has a REAL value (never blank, never "?")
 * 5. Services are pre-selected — user never sees "pick a service"
 * 6. Output variable names use the actual API response field paths
 * 7. Trigger inputFields should be EMPTY for webhooks — the AI extraction node declares outputs
 * 8. AI prompts for extraction should say "find the X regardless of format" to handle variations
 */

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function serpApiNode(
  id: string,
  label: string,
  y: number,
  query: string,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "GET",
        url: `https://serpapi.com/search.json?q=${encodeURIComponent(query)}&api_key={serpapi_key}`,
        headers: [],
        body: [],
        variables: vars,
      },
    },
    x,
  );
}
function ga4Node(
  id: string,
  label: string,
  y: number,
  dimensions: string,
  metrics: string,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://analyticsdata.googleapis.com/v1beta/properties/{ga4_property_id}:runReport",
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "dimensions", value: dimensions },
          { key: "metrics", value: metrics },
          {
            key: "dateRanges",
            value: '[{"startDate":"90daysAgo","endDate":"today"}]',
          },
        ],
        variables: vars,
      },
    },
    x,
  );
}
function bufferNode(
  id: string,
  label: string,
  y: number,
  text: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.bufferapp.com/1/updates/create.json",
        headers: [
          { key: "Authorization", value: "Bearer {buffer_token}" },
          { key: "Content-Type", value: "application/x-www-form-urlencoded" },
        ],
        body: [
          { key: "text", value: text },
          { key: "profile_ids[]", value: "{buffer_profile_id}" },
          { key: "scheduled_at", value: "{scheduled_time}" },
        ],
        variables: [
          { name: "success", customName: "buffer_success", type: "boolean" },
          {
            name: "updates[0].id",
            customName: "buffer_update_id",
            type: "string",
          },
        ],
      },
    },
    x,
  );
}

// =============================================================================
// ELITE BATCH 12 PART 3 — TEMPLATES 1088–1107
// =============================================================================

export const ELITE_BATCH12_PART3: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ===================================================================
  // 1088. SCHEMA OPPORTUNITY FINDER (8 nodes)
  // ===================================================================

  "Schema Opportunity Finder": {
    summary:
      "Scans competitor rich results via SerpAPI, extracts their structured data markup, audits your own site schema with code, identifies schema gaps, generates AI recommendations for missing schema types, builds an implementation guide in Sheets, and hands off to the dev team via Slack.",
    nodes: [
      trigger(
        "Webhook: Competitor URL submitted for schema analysis",
        "webhook_app",
        {
          source: "internal",
          event: "schema_audit_request",
          howItStarts:
            "A competitor URL is submitted to trigger a full rich result and schema markup comparison against your own site",
          setup:
            "Connect SerpAPI for SERP rich result scanning. Provide your site domain for schema comparison. Configure Sheets for report output and Slack for dev notifications.",
          inputFields: [],
        },
      ),

      // 2. SERP rich result scanner (SerpAPI)
      n("2", "hitApi", "SerpAPI: Scan competitor rich results", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://serpapi.com/search.json?q=site:{competitor_domain}&api_key={serpapi_key}&num=50&gl=us",
          headers: [],
          body: [],
          variables: [
            {
              name: "organic_results",
              customName: "competitor_serp_results",
              type: "string",
            },
            {
              name: "rich_snippets",
              customName: "competitor_rich_snippets",
              type: "string",
            },
            {
              name: "search_information.total_results",
              customName: "total_indexed_pages",
              type: "number",
            },
          ],
        },
      }),

      // 3. Competitor schema extractor (runCode)
      n("3", "runCode", "Extract competitor schema types from SERP data", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Extract schema types present in competitor rich results
const results = JSON.parse(vars.competitor_serp_results || "[]");
const richSnippets = JSON.parse(vars.competitor_rich_snippets || "[]");
const schemaTypes = new Set();
const schemaPages = [];
results.forEach(r => {
  if (r.rich_snippet) {
    const types = [];
    if (r.rich_snippet.top) types.push(...Object.keys(r.rich_snippet.top));
    if (r.rich_snippet.bottom) types.push(...Object.keys(r.rich_snippet.bottom));
    types.forEach(t => schemaTypes.add(t));
    schemaPages.push({ url: r.link, title: r.title, schemas: types });
  }
  if (r.sitelinks) schemaTypes.add("SiteNavigationElement");
  if (r.thumbnail) schemaTypes.add("ImageObject");
});
const detected = Array.from(schemaTypes);
return {
  competitor_schema_types_json: JSON.stringify(detected),
  competitor_schema_count: detected.length,
  competitor_schema_pages_json: JSON.stringify(schemaPages.slice(0, 20)),
  competitor_pages_with_schema: schemaPages.length
};`,
          outputVariables: [
            { name: "competitor_schema_types_json", type: "string" },
            { name: "competitor_schema_count", type: "number" },
            { name: "competitor_schema_pages_json", type: "string" },
            { name: "competitor_pages_with_schema", type: "number" },
          ],
        },
      }),

      // 4. Your site schema auditor (runCode)
      n("4", "runCode", "Audit your site schema coverage", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Compare competitor schemas against your site's known schema implementation
const competitorSchemas = JSON.parse(vars.competitor_schema_types_json || "[]");
const payload = JSON.parse(vars.webhook_payload || "{}");
const yourSchemas = (payload.your_schemas || "Article,Organization,WebSite,BreadcrumbList").split(",").map(s => s.trim());
const missing = competitorSchemas.filter(s => !yourSchemas.includes(s));
const shared = competitorSchemas.filter(s => yourSchemas.includes(s));
const yourOnly = yourSchemas.filter(s => !competitorSchemas.includes(s));
return {
  missing_schemas_json: JSON.stringify(missing),
  missing_schema_count: missing.length,
  shared_schemas_json: JSON.stringify(shared),
  shared_schema_count: shared.length,
  your_only_schemas_json: JSON.stringify(yourOnly),
  gap_severity: missing.length > 5 ? "high" : missing.length > 2 ? "medium" : "low"
};`,
          outputVariables: [
            { name: "missing_schemas_json", type: "string" },
            { name: "missing_schema_count", type: "number" },
            { name: "shared_schemas_json", type: "string" },
            { name: "shared_schema_count", type: "number" },
            { name: "your_only_schemas_json", type: "string" },
            { name: "gap_severity", type: "string" },
          ],
        },
      }),

      // 5. Schema gap identifier (condition)
      n("5", "condition", "Are there actionable schema gaps?", 1000, {
        conditionData: [
          {
            variable: "missing_schema_count",
            value: "0",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. AI schema recommendation
      aiNode(
        "6",
        "AI: Generate schema implementation recommendations",
        1240,
        "You are a technical SEO expert specializing in structured data markup. Recommend specific schema.org types with priority, estimated traffic impact, and implementation complexity. Return JSON.",
        "Analyze schema gaps for a site. Missing schemas vs competitor: {missing_schemas_json} ({missing_schema_count} types). Shared schemas: {shared_schemas_json}. Competitor has {competitor_pages_with_schema} pages with schema out of {total_indexed_pages} indexed. Gap severity: {gap_severity}. For each missing schema, return recommendations (array of {schema_type, priority, traffic_impact_estimate, implementation_complexity, example_usage, pages_to_target}), executive_summary (string), estimated_ctr_lift (string), implementation_order (array of schema type strings ranked by ROI).",
        [
          {
            field: "recommendations",
            as: "schema_recommendations_json",
            type: "string",
          },
          {
            field: "executive_summary",
            as: "schema_executive_summary",
            type: "string",
          },
          {
            field: "estimated_ctr_lift",
            as: "estimated_ctr_lift",
            type: "string",
          },
          {
            field: "implementation_order",
            as: "implementation_order_json",
            type: "string",
          },
        ],
      ),

      // 7. Schema implementation guide (Sheets)
      sheetsNode(
        "7",
        "Log schema recommendations to Sheets",
        1480,
        "Schema_Gaps",
        `[["${new Date().toISOString()}","{competitor_domain}","{missing_schema_count}","{shared_schema_count}","{gap_severity}","{schema_executive_summary}","{estimated_ctr_lift}","{implementation_order_json}"]]`,
      ),

      // 8. Dev team handoff (Slack)
      slackNode(
        "8",
        "Slack: Notify dev team of schema opportunities",
        1720,
        "Schema Opportunity Report\\n=========================\\nCompetitor: {competitor_domain}\\nMissing Schema Types: {missing_schema_count}\\nGap Severity: {gap_severity}\\nEstimated CTR Lift: {estimated_ctr_lift}\\n\\nSummary: {schema_executive_summary}\\n\\nImplementation Order: {implementation_order_json}\\n\\nFull report in Sheets.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // gaps found -> AI recommendations
      e("5", "7", "output_2"), // no gaps -> log status to Sheets
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1089. META GAP REPORT (7 nodes)
  // ===================================================================

  "Meta Gap Report": {
    summary:
      "Crawls site pages on schedule, extracts meta tags with code, evaluates meta quality via AI, rewrites underperforming metas, ranks fixes by priority, builds a gap report in Sheets, and distributes to the team via Slack.",
    nodes: [
      trigger("Schedule: Weekly site meta tag audit", "schedule", {
        schedule: "0 6 * * 1 (every Monday at 6 AM)",
        howItStarts:
          "A scheduled crawl fetches pages from your sitemap and initiates a comprehensive meta tag quality audit across all indexed pages",
        setup:
          "Provide your sitemap URL for page discovery. Configure OpenAI for meta quality evaluation and rewriting. Set up Sheets for report output and Slack for team notifications.",
        inputFields: [],
      }),

      // 2. Meta tag extractor (runCode)
      n(
        "2",
        "runCode",
        "Extract and catalog meta tags from crawled pages",
        280,
        {
          runCodeDetails: {
            language: "javascript",
            code: `// Extract meta tags from crawled page data
const payload = JSON.parse(vars.webhook_payload || "{}");
const pages = payload.pages || [];
const results = pages.map(p => {
  const title = p.title || "";
  const desc = p.meta_description || "";
  const og_title = p.og_title || "";
  const og_desc = p.og_description || "";
  const canonical = p.canonical || "";
  const issues = [];
  if (!title) issues.push("missing_title");
  else if (title.length < 30) issues.push("title_too_short");
  else if (title.length > 60) issues.push("title_too_long");
  if (!desc) issues.push("missing_description");
  else if (desc.length < 70) issues.push("description_too_short");
  else if (desc.length > 160) issues.push("description_too_long");
  if (!og_title) issues.push("missing_og_title");
  if (!og_desc) issues.push("missing_og_description");
  if (!canonical) issues.push("missing_canonical");
  return { url: p.url, title, desc, og_title, og_desc, canonical, issues, issue_count: issues.length };
});
const withIssues = results.filter(r => r.issue_count > 0);
const sorted = withIssues.sort((a, b) => b.issue_count - a.issue_count);
return {
  meta_audit_json: JSON.stringify(sorted.slice(0, 50)),
  total_pages_audited: results.length,
  pages_with_issues: withIssues.length,
  total_issues_found: withIssues.reduce((sum, r) => sum + r.issue_count, 0),
  worst_page_url: sorted.length > 0 ? sorted[0].url : "none"
};`,
            outputVariables: [
              { name: "meta_audit_json", type: "string" },
              { name: "total_pages_audited", type: "number" },
              { name: "pages_with_issues", type: "number" },
              { name: "total_issues_found", type: "number" },
              { name: "worst_page_url", type: "string" },
            ],
          },
        },
      ),

      // 3. Meta quality evaluator (AI)
      aiNode(
        "3",
        "AI: Evaluate meta tag quality and impact",
        520,
        "You are an SEO meta tag specialist. Evaluate meta tags for keyword relevance, click-through potential, uniqueness, and search intent alignment. Score each page and identify the most impactful fixes. Return JSON.",
        "Evaluate these page meta tags for quality: {meta_audit_json}. Total pages audited: {total_pages_audited}, pages with issues: {pages_with_issues}. For each page with issues, score meta_quality (1-10), identify primary_issue, and estimate fix_impact (high/medium/low). Return evaluated_pages (array of {url, meta_quality_score, primary_issue, fix_impact, fix_priority_rank}), overall_health_score (number 1-100), critical_fixes_count (number), quick_wins_count (number).",
        [
          {
            field: "evaluated_pages",
            as: "evaluated_pages_json",
            type: "string",
          },
          {
            field: "overall_health_score",
            as: "meta_health_score",
            type: "number",
          },
          {
            field: "critical_fixes_count",
            as: "critical_fixes_count",
            type: "number",
          },
          { field: "quick_wins_count", as: "quick_wins_count", type: "number" },
        ],
      ),

      // 4. AI meta rewriter
      aiNode(
        "4",
        "AI: Rewrite underperforming meta tags",
        760,
        "You are a conversion-focused SEO copywriter. Write meta titles (50-60 chars) and descriptions (120-155 chars) that maximize CTR while incorporating target keywords naturally. Return JSON.",
        "Rewrite meta tags for these underperforming pages: {evaluated_pages_json}. Focus on pages with fix_impact=high first. For each, create optimized_title (50-60 chars), optimized_description (120-155 chars), target_keyword, and expected_ctr_improvement. Return rewrites (array of {url, optimized_title, optimized_description, target_keyword, expected_ctr_improvement}), total_rewrites (number).",
        [
          { field: "rewrites", as: "meta_rewrites_json", type: "string" },
          { field: "total_rewrites", as: "total_rewrites", type: "number" },
        ],
      ),

      // 5. Priority ranker (runCode)
      n("5", "runCode", "Rank meta fixes by business impact priority", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Rank all fixes by estimated impact
const rewrites = JSON.parse(vars.meta_rewrites_json || "[]");
const evaluated = JSON.parse(vars.evaluated_pages_json || "[]");
const merged = rewrites.map(r => {
  const eval_data = evaluated.find(ep => ep.url === r.url) || {};
  return { ...r, meta_quality_score: eval_data.meta_quality_score || 0, fix_impact: eval_data.fix_impact || "medium", priority: eval_data.fix_priority_rank || 999 };
});
merged.sort((a, b) => a.priority - b.priority);
const high = merged.filter(m => m.fix_impact === "high");
const medium = merged.filter(m => m.fix_impact === "medium");
const low = merged.filter(m => m.fix_impact === "low");
return {
  prioritized_fixes_json: JSON.stringify(merged),
  high_priority_count: high.length,
  medium_priority_count: medium.length,
  low_priority_count: low.length,
  top_fix_url: merged.length > 0 ? merged[0].url : "none"
};`,
          outputVariables: [
            { name: "prioritized_fixes_json", type: "string" },
            { name: "high_priority_count", type: "number" },
            { name: "medium_priority_count", type: "number" },
            { name: "low_priority_count", type: "number" },
            { name: "top_fix_url", type: "string" },
          ],
        },
      }),

      // 6. Gap report builder (Sheets)
      sheetsNode(
        "6",
        "Log meta gap report to Sheets",
        1240,
        "Meta_Gaps",
        `[["${new Date().toISOString()}","{total_pages_audited}","{pages_with_issues}","{total_issues_found}","{meta_health_score}","{high_priority_count}","{medium_priority_count}","{total_rewrites}","{top_fix_url}"]]`,
      ),

      // 7. Team distribution (Slack)
      slackNode(
        "7",
        "Slack: Distribute meta gap report to team",
        1480,
        "Meta Tag Gap Report\\n====================\\nPages Audited: {total_pages_audited}\\nPages with Issues: {pages_with_issues} ({total_issues_found} total issues)\\nHealth Score: {meta_health_score}/100\\n\\nPriority Breakdown:\\n- High: {high_priority_count}\\n- Medium: {medium_priority_count}\\n- Low: {low_priority_count}\\n\\nRewrites Generated: {total_rewrites}\\nTop Priority Fix: {top_fix_url}\\n\\nFull report and rewritten metas in Sheets.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1090. INTERNAL LINKING MAP (8 nodes)
  // ===================================================================

  "Internal Linking Map": {
    summary:
      "Crawls the site to build a link graph with code, detects orphaned pages, analyzes link authority flow, uses AI to recommend linking opportunities, validates topical clusters, generates a comprehensive internal linking report in Sheets, and hands off to the implementation team via Slack.",
    nodes: [
      trigger("Schedule: Weekly internal link audit", "schedule", {
        schedule: "0 5 * * 0 (every Sunday at 5 AM)",
        howItStarts:
          "A scheduled crawl builds a full internal link graph from your sitemap and page content to identify orphaned pages and linking opportunities",
        setup:
          "Provide your sitemap URL or crawl data source. Configure OpenAI for link opportunity analysis. Set up Sheets for the linking map and Slack for team notifications.",
        inputFields: [],
      }),

      // 2. Link graph builder (runCode)
      n("2", "runCode", "Build internal link graph from crawl data", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Build a link graph from crawled page data
const payload = JSON.parse(vars.webhook_payload || "{}");
const pages = payload.pages || [];
const graph = {};
const inboundCount = {};
const outboundCount = {};
pages.forEach(p => {
  const url = p.url || "";
  const links = p.internal_links || [];
  graph[url] = links;
  outboundCount[url] = links.length;
  links.forEach(link => {
    inboundCount[link] = (inboundCount[link] || 0) + 1;
  });
});
const allUrls = new Set([...Object.keys(graph), ...Object.keys(inboundCount)]);
const pageMetrics = Array.from(allUrls).map(url => ({
  url,
  inbound: inboundCount[url] || 0,
  outbound: outboundCount[url] || 0,
  ratio: (inboundCount[url] || 0) / Math.max(outboundCount[url] || 1, 1)
}));
pageMetrics.sort((a, b) => a.inbound - b.inbound);
return {
  link_graph_json: JSON.stringify(pageMetrics.slice(0, 50)),
  total_pages: allUrls.size,
  avg_inbound: Math.round(Object.values(inboundCount).reduce((s, v) => s + v, 0) / Math.max(allUrls.size, 1)),
  avg_outbound: Math.round(Object.values(outboundCount).reduce((s, v) => s + v, 0) / Math.max(Object.keys(graph).length, 1)),
  min_inbound_url: pageMetrics.length > 0 ? pageMetrics[0].url : "none"
};`,
          outputVariables: [
            { name: "link_graph_json", type: "string" },
            { name: "total_pages", type: "number" },
            { name: "avg_inbound", type: "number" },
            { name: "avg_outbound", type: "number" },
            { name: "min_inbound_url", type: "string" },
          ],
        },
      }),

      // 3. Orphaned page detector (runCode)
      n("3", "runCode", "Detect orphaned and under-linked pages", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Identify orphaned pages (0-1 inbound links) and under-linked pages
const metrics = JSON.parse(vars.link_graph_json || "[]");
const orphaned = metrics.filter(p => p.inbound === 0);
const underLinked = metrics.filter(p => p.inbound === 1);
const overLinked = metrics.filter(p => p.outbound > 50);
const isolated = metrics.filter(p => p.inbound === 0 && p.outbound === 0);
return {
  orphaned_pages_json: JSON.stringify(orphaned.slice(0, 20)),
  orphaned_count: orphaned.length,
  under_linked_pages_json: JSON.stringify(underLinked.slice(0, 20)),
  under_linked_count: underLinked.length,
  over_linked_count: overLinked.length,
  isolated_count: isolated.length,
  link_health: orphaned.length === 0 ? "excellent" : orphaned.length <= 5 ? "good" : orphaned.length <= 15 ? "needs_attention" : "critical"
};`,
          outputVariables: [
            { name: "orphaned_pages_json", type: "string" },
            { name: "orphaned_count", type: "number" },
            { name: "under_linked_pages_json", type: "string" },
            { name: "under_linked_count", type: "number" },
            { name: "over_linked_count", type: "number" },
            { name: "isolated_count", type: "number" },
            { name: "link_health", type: "string" },
          ],
        },
      }),

      // 4. Link authority flow analyzer (runCode)
      n("4", "runCode", "Analyze link authority flow and distribution", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Simplified PageRank-style authority flow analysis
const metrics = JSON.parse(vars.link_graph_json || "[]");
const totalPages = parseInt(vars.total_pages) || 1;
const avgInbound = parseInt(vars.avg_inbound) || 1;
const authorityScores = metrics.map(p => {
  const score = Math.round((p.inbound / Math.max(avgInbound, 1)) * 50 + (p.ratio * 25) + (p.outbound > 0 ? 25 : 0));
  return { url: p.url, authority_score: Math.min(score, 100), inbound: p.inbound, outbound: p.outbound };
});
authorityScores.sort((a, b) => b.authority_score - a.authority_score);
const topAuthority = authorityScores.slice(0, 10);
const lowAuthority = authorityScores.filter(p => p.authority_score < 20);
return {
  authority_scores_json: JSON.stringify(authorityScores.slice(0, 30)),
  top_authority_pages_json: JSON.stringify(topAuthority),
  low_authority_count: lowAuthority.length,
  authority_distribution: topAuthority.length > 0 ? "top_heavy" : "flat",
  highest_authority_url: topAuthority.length > 0 ? topAuthority[0].url : "none",
  highest_authority_score: topAuthority.length > 0 ? topAuthority[0].authority_score : 0
};`,
          outputVariables: [
            { name: "authority_scores_json", type: "string" },
            { name: "top_authority_pages_json", type: "string" },
            { name: "low_authority_count", type: "number" },
            { name: "authority_distribution", type: "string" },
            { name: "highest_authority_url", type: "string" },
            { name: "highest_authority_score", type: "number" },
          ],
        },
      }),

      // 5. AI linking opportunity recommender
      aiNode(
        "5",
        "AI: Recommend internal linking opportunities",
        1000,
        "You are an internal linking strategist. Analyze link graphs to recommend contextually relevant internal links that distribute authority effectively and improve topical relevance. Prioritize linking from high-authority pages to orphaned/under-linked pages. Return JSON.",
        "Site link analysis: {total_pages} pages, avg {avg_inbound} inbound links. Orphaned pages ({orphaned_count}): {orphaned_pages_json}. Under-linked ({under_linked_count}): {under_linked_pages_json}. Top authority pages: {top_authority_pages_json}. Link health: {link_health}. Recommend specific internal links to add. Return link_recommendations (array of {source_url, target_url, anchor_text_suggestion, rationale, priority}), total_recommendations (number), estimated_authority_improvement (string), implementation_phases (array of {phase, description, link_count}).",
        [
          {
            field: "link_recommendations",
            as: "link_recommendations_json",
            type: "string",
          },
          {
            field: "total_recommendations",
            as: "total_link_recommendations",
            type: "number",
          },
          {
            field: "estimated_authority_improvement",
            as: "estimated_authority_improvement",
            type: "string",
          },
          {
            field: "implementation_phases",
            as: "implementation_phases_json",
            type: "string",
          },
        ],
      ),

      // 6. Topical cluster validator (runCode)
      n(
        "6",
        "runCode",
        "Validate linking recommendations against topical clusters",
        1240,
        {
          runCodeDetails: {
            language: "javascript",
            code: `// Validate that link recommendations respect topical clusters
const recs = JSON.parse(vars.link_recommendations_json || "[]");
const validated = recs.map(r => {
  const sourceSlug = (r.source_url || "").split("/").filter(Boolean).pop() || "";
  const targetSlug = (r.target_url || "").split("/").filter(Boolean).pop() || "";
  const topicalMatch = sourceSlug.split("-").some(w => targetSlug.includes(w)) || targetSlug.split("-").some(w => sourceSlug.includes(w));
  return { ...r, topical_match: topicalMatch, validation_status: topicalMatch ? "confirmed" : "review_needed" };
});
const confirmed = validated.filter(v => v.validation_status === "confirmed");
const needsReview = validated.filter(v => v.validation_status === "review_needed");
return {
  validated_recs_json: JSON.stringify(validated),
  confirmed_count: confirmed.length,
  review_needed_count: needsReview.length,
  validation_rate: validated.length > 0 ? Math.round((confirmed.length / validated.length) * 100) : 0
};`,
            outputVariables: [
              { name: "validated_recs_json", type: "string" },
              { name: "confirmed_count", type: "number" },
              { name: "review_needed_count", type: "number" },
              { name: "validation_rate", type: "number" },
            ],
          },
        },
      ),

      // 7. Internal linking report (Sheets)
      sheetsNode(
        "7",
        "Log internal linking map to Sheets",
        1480,
        "Internal_Links",
        `[["${new Date().toISOString()}","{total_pages}","{orphaned_count}","{under_linked_count}","{link_health}","{total_link_recommendations}","{confirmed_count}","{review_needed_count}","{estimated_authority_improvement}"]]`,
      ),

      // 8. Implementation handoff (Slack)
      slackNode(
        "8",
        "Slack: Hand off internal linking plan to team",
        1720,
        "Internal Linking Map Report\\n============================\\nTotal Pages: {total_pages}\\nOrphaned Pages: {orphaned_count}\\nUnder-linked: {under_linked_count}\\nLink Health: {link_health}\\n\\nRecommendations: {total_link_recommendations} links\\n- Confirmed: {confirmed_count}\\n- Needs Review: {review_needed_count}\\n- Validation Rate: {validation_rate}%\\n\\nAuthority Improvement: {estimated_authority_improvement}\\n\\nFull linking map and recommendations in Sheets.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1091. STALE CONTENT DETECTOR (8 nodes)
  // ===================================================================

  "Stale Content Detector": {
    summary:
      "Scans content freshness on schedule, fetches traffic trends from GA4, tracks ranking positions via SerpAPI, scores staleness severity with code, generates AI update briefs, compares competitor freshness, builds a stale content report in Sheets, and assigns work to the content team via Slack.",
    nodes: [
      trigger("Schedule: Weekly content freshness scan", "schedule", {
        schedule: "0 7 * * 2 (every Tuesday at 7 AM)",
        howItStarts:
          "A scheduled job scans all published content for freshness indicators including last modified date, traffic trends, and ranking changes to identify stale content needing updates",
        setup:
          "Connect GA4 for traffic trend analysis. Configure SerpAPI for ranking position tracking. Provide content inventory with last-modified dates. Set up Sheets for reporting and Slack for content team assignments.",
        inputFields: [],
      }),

      // 2. Traffic trend fetch (GA4)
      ga4Node(
        "2",
        "GA4: Fetch page traffic trends",
        280,
        '[{"name":"pagePath"},{"name":"date"}]',
        '[{"name":"sessions"},{"name":"screenPageViews"},{"name":"bounceRate"}]',
        [
          { name: "rows", customName: "ga4_traffic_rows", type: "string" },
          { name: "rowCount", customName: "ga4_page_count", type: "number" },
        ],
      ),

      // 3. Ranking position tracker (SerpAPI)
      n("3", "hitApi", "SerpAPI: Track keyword ranking positions", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://serpapi.com/search.json?q=site:{your_domain}&api_key={serpapi_key}&num=100&gl=us",
          headers: [],
          body: [],
          variables: [
            {
              name: "organic_results",
              customName: "ranking_results_json",
              type: "string",
            },
            {
              name: "search_information.total_results",
              customName: "total_ranking_pages",
              type: "number",
            },
          ],
        },
      }),

      // 4. Staleness severity scorer (runCode)
      n("4", "runCode", "Score content staleness severity", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Score staleness based on traffic trends, age, and ranking data
const trafficRows = JSON.parse(vars.ga4_traffic_rows || "[]");
const rankings = JSON.parse(vars.ranking_results_json || "[]");
const now = Date.now();
const pageScores = {};
trafficRows.forEach(row => {
  const dims = row.dimensionValues || [];
  const mets = row.metricValues || [];
  const path = dims[0] ? dims[0].value : "";
  const sessions = mets[0] ? parseInt(mets[0].value) : 0;
  if (!pageScores[path]) pageScores[path] = { path, total_sessions: 0, data_points: 0 };
  pageScores[path].total_sessions += sessions;
  pageScores[path].data_points++;
});
const scored = Object.values(pageScores).map(p => {
  const avgSessions = p.total_sessions / Math.max(p.data_points, 1);
  const rankData = rankings.find(r => (r.link || "").includes(p.path));
  const position = rankData ? rankData.position || 50 : 50;
  const trafficDecline = avgSessions < 5 ? 40 : avgSessions < 20 ? 20 : 0;
  const rankPenalty = position > 20 ? 30 : position > 10 ? 15 : 0;
  const staleness = Math.min(trafficDecline + rankPenalty + 10, 100);
  return { path: p.path, staleness_score: staleness, avg_sessions: Math.round(avgSessions), position, severity: staleness > 70 ? "critical" : staleness > 40 ? "moderate" : "low" };
});
scored.sort((a, b) => b.staleness_score - a.staleness_score);
const critical = scored.filter(s => s.severity === "critical");
const moderate = scored.filter(s => s.severity === "moderate");
return {
  stale_content_json: JSON.stringify(scored.slice(0, 30)),
  critical_stale_count: critical.length,
  moderate_stale_count: moderate.length,
  total_scored: scored.length,
  most_stale_path: scored.length > 0 ? scored[0].path : "none",
  highest_staleness_score: scored.length > 0 ? scored[0].staleness_score : 0
};`,
          outputVariables: [
            { name: "stale_content_json", type: "string" },
            { name: "critical_stale_count", type: "number" },
            { name: "moderate_stale_count", type: "number" },
            { name: "total_scored", type: "number" },
            { name: "most_stale_path", type: "string" },
            { name: "highest_staleness_score", type: "number" },
          ],
        },
      }),

      // 5. AI update brief generator
      aiNode(
        "5",
        "AI: Generate content update briefs",
        1000,
        "You are a content strategist specializing in content refresh and updating. Create actionable update briefs that specify exactly what to change, add, or remove to improve freshness, relevance, and rankings. Return JSON.",
        "Generate update briefs for stale content. Critical ({critical_stale_count}) and moderate ({moderate_stale_count}) stale pages: {stale_content_json}. Most stale: {most_stale_path} (score: {highest_staleness_score}). For each critical/moderate page, create update_brief with path, update_type (full_rewrite/partial_update/data_refresh), specific_changes (array of strings), estimated_effort_hours (number), expected_traffic_recovery (string). Return update_briefs (array), total_briefs (number), total_estimated_hours (number), priority_order (array of paths).",
        [
          { field: "update_briefs", as: "update_briefs_json", type: "string" },
          { field: "total_briefs", as: "total_update_briefs", type: "number" },
          {
            field: "total_estimated_hours",
            as: "total_estimated_hours",
            type: "number",
          },
          {
            field: "priority_order",
            as: "update_priority_order_json",
            type: "string",
          },
        ],
      ),

      // 6. Competitor freshness comparison
      aiNode(
        "6",
        "AI: Compare competitor content freshness",
        1240,
        "You are a competitive content analyst. Compare the freshness of content on the stale pages against what competitors are publishing on similar topics. Identify gaps and urgency. Return JSON.",
        "The site has {critical_stale_count} critically stale and {moderate_stale_count} moderately stale pages. Most stale: {most_stale_path}. Stale content details: {stale_content_json}. Analyze typical competitor update frequency for these topics and estimate how far behind we are. Return competitor_comparison (array of {path, competitor_update_frequency, our_gap_days_estimate, urgency}), average_competitor_refresh_days (number), competitive_risk_level (string), freshness_strategy (string).",
        [
          {
            field: "competitor_comparison",
            as: "competitor_freshness_json",
            type: "string",
          },
          {
            field: "average_competitor_refresh_days",
            as: "avg_competitor_refresh_days",
            type: "number",
          },
          {
            field: "competitive_risk_level",
            as: "competitive_risk_level",
            type: "string",
          },
          {
            field: "freshness_strategy",
            as: "freshness_strategy",
            type: "string",
          },
        ],
      ),

      // 7. Stale content report (Sheets)
      sheetsNode(
        "7",
        "Log stale content report to Sheets",
        1480,
        "Stale_Content",
        `[["${new Date().toISOString()}","{total_scored}","{critical_stale_count}","{moderate_stale_count}","{most_stale_path}","{highest_staleness_score}","{total_update_briefs}","{total_estimated_hours}","{competitive_risk_level}","{freshness_strategy}"]]`,
      ),

      // 8. Content team assignment (Slack)
      slackNode(
        "8",
        "Slack: Assign stale content updates to team",
        1720,
        "Stale Content Report\\n=====================\\nPages Scored: {total_scored}\\nCritical: {critical_stale_count}\\nModerate: {moderate_stale_count}\\nMost Stale: {most_stale_path} (score: {highest_staleness_score}/100)\\n\\nUpdate Briefs Generated: {total_update_briefs}\\nEstimated Hours: {total_estimated_hours}\\n\\nCompetitor Risk: {competitive_risk_level}\\nAvg Competitor Refresh: {avg_competitor_refresh_days} days\\nStrategy: {freshness_strategy}\\n\\nFull briefs and assignments in Sheets.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1092. TOPICAL CLUSTER PLANNER (9 nodes)
  // ===================================================================

  "Topical Cluster Planner": {
    summary:
      "Takes a topic seed, builds a keyword universe via SerpAPI, uses AI to architect topical clusters, maps search intent for each cluster, validates content gaps with code, generates AI content briefs, plans the publication sequence, logs the cluster plan to Sheets, and kicks off the team via Slack.",
    nodes: [
      trigger(
        "Webhook: Topic seed submitted for cluster planning",
        "webhook_app",
        {
          source: "internal",
          event: "cluster_plan_request",
          howItStarts:
            "A topic seed keyword is submitted to generate a full topical cluster plan including keyword research, content briefs, and publication sequence",
          setup:
            "Configure SerpAPI for keyword research. Set up OpenAI for cluster architecture and brief generation. Provide Sheets for plan output and Slack for team kick-off.",
          inputFields: [],
        },
      ),

      // 2. Keyword universe builder (SerpAPI)
      n("2", "hitApi", "SerpAPI: Build keyword universe from topic seed", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://serpapi.com/search.json?q={topic_seed}&api_key={serpapi_key}&engine=google_autocomplete",
          headers: [],
          body: [],
          variables: [
            {
              name: "suggestions",
              customName: "autocomplete_suggestions_json",
              type: "string",
            },
          ],
        },
      }),

      // 3. AI cluster architect
      aiNode(
        "3",
        "AI: Architect topical clusters from keywords",
        520,
        "You are a topical authority and content clustering expert. Design hub-and-spoke content clusters that establish topical authority. Each cluster needs a pillar page and supporting content pieces. Return JSON.",
        "Build topical clusters for seed topic from webhook: {webhook_payload}. Keyword suggestions: {autocomplete_suggestions_json}. Design a comprehensive cluster architecture. Return pillar_topic (string), clusters (array of {cluster_name, pillar_page_title, supporting_articles (array of {title, target_keyword, search_intent}), estimated_total_volume}), total_clusters (number), total_content_pieces (number), estimated_months_to_complete (number).",
        [
          { field: "pillar_topic", as: "pillar_topic", type: "string" },
          { field: "clusters", as: "clusters_json", type: "string" },
          { field: "total_clusters", as: "total_clusters", type: "number" },
          {
            field: "total_content_pieces",
            as: "total_content_pieces",
            type: "number",
          },
          {
            field: "estimated_months_to_complete",
            as: "estimated_months",
            type: "number",
          },
        ],
      ),

      // 4. Search intent mapper
      aiNode(
        "4",
        "AI: Map search intent for each cluster piece",
        760,
        "You are a search intent analyst. Classify each content piece by search intent (informational, navigational, commercial, transactional) and recommend the ideal content format (guide, listicle, comparison, tutorial, tool, etc.). Return JSON.",
        "Map search intent for all content pieces in these clusters: {clusters_json}. Pillar topic: {pillar_topic}. Total pieces: {total_content_pieces}. For each piece, classify intent_type, recommend content_format, estimate word_count, and note funnel_stage (top/middle/bottom). Return intent_mapped_pieces (array of {title, target_keyword, intent_type, content_format, word_count, funnel_stage}), intent_distribution (object with counts per intent type), format_distribution (object with counts per format).",
        [
          {
            field: "intent_mapped_pieces",
            as: "intent_mapped_json",
            type: "string",
          },
          {
            field: "intent_distribution",
            as: "intent_distribution_json",
            type: "string",
          },
          {
            field: "format_distribution",
            as: "format_distribution_json",
            type: "string",
          },
        ],
      ),

      // 5. Content gap validator (runCode)
      n(
        "5",
        "runCode",
        "Validate content gaps and deduplicate against existing content",
        1000,
        {
          runCodeDetails: {
            language: "javascript",
            code: `// Validate cluster pieces against existing content to avoid duplication
const pieces = JSON.parse(vars.intent_mapped_json || "[]");
const payload = JSON.parse(vars.webhook_payload || "{}");
const existingUrls = (payload.existing_content || "").split(",").map(u => u.trim().toLowerCase());
const validated = pieces.map(p => {
  const titleWords = (p.title || "").toLowerCase().split(/\\s+/);
  const isDuplicate = existingUrls.some(url => titleWords.filter(w => w.length > 3).some(w => url.includes(w)));
  return { ...p, is_new: !isDuplicate, gap_status: isDuplicate ? "exists" : "gap" };
});
const gaps = validated.filter(v => v.is_new);
const existing = validated.filter(v => !v.is_new);
return {
  validated_pieces_json: JSON.stringify(validated),
  new_content_gaps: gaps.length,
  existing_coverage: existing.length,
  gap_percentage: pieces.length > 0 ? Math.round((gaps.length / pieces.length) * 100) : 0,
  needs_content: gaps.length > 0 ? "yes" : "no"
};`,
            outputVariables: [
              { name: "validated_pieces_json", type: "string" },
              { name: "new_content_gaps", type: "number" },
              { name: "existing_coverage", type: "number" },
              { name: "gap_percentage", type: "number" },
              { name: "needs_content", type: "string" },
            ],
          },
        },
      ),

      // 6. AI content brief generator
      aiNode(
        "6",
        "AI: Generate detailed content briefs for gaps",
        1240,
        "You are an SEO content brief writer. Create detailed, actionable content briefs that include outline, target keyword, secondary keywords, word count, CTA, and internal linking suggestions. Return JSON.",
        "Generate content briefs for these new content gaps: {validated_pieces_json}. Only create briefs for pieces with gap_status=gap. Pillar topic: {pillar_topic}. For each gap, create brief with title, target_keyword, secondary_keywords (array), outline (array of H2 headings), target_word_count, recommended_cta, internal_link_targets (array). Return content_briefs (array), total_briefs_created (number), total_word_count_estimate (number).",
        [
          {
            field: "content_briefs",
            as: "content_briefs_json",
            type: "string",
          },
          {
            field: "total_briefs_created",
            as: "total_briefs_created",
            type: "number",
          },
          {
            field: "total_word_count_estimate",
            as: "total_word_count_estimate",
            type: "number",
          },
        ],
      ),

      // 7. Publication sequence planner
      aiNode(
        "7",
        "AI: Plan optimal publication sequence",
        1480,
        "You are a content publication strategist. Plan the optimal order to publish cluster content, starting with the pillar page and sequencing supporting content for maximum SEO impact and internal linking efficiency. Return JSON.",
        "Plan publication sequence for {total_briefs_created} content briefs in the {pillar_topic} cluster. Briefs: {content_briefs_json}. Intent distribution: {intent_distribution_json}. Consider publishing pillar first, then supporting content in order of search volume and internal linking dependencies. Return publication_sequence (array of {week, title, content_type, rationale}), total_weeks (number), recommended_frequency (string), first_publish_title (string).",
        [
          {
            field: "publication_sequence",
            as: "publication_sequence_json",
            type: "string",
          },
          {
            field: "total_weeks",
            as: "total_publication_weeks",
            type: "number",
          },
          {
            field: "recommended_frequency",
            as: "recommended_frequency",
            type: "string",
          },
          {
            field: "first_publish_title",
            as: "first_publish_title",
            type: "string",
          },
        ],
      ),

      // 8. Cluster plan report (Sheets)
      sheetsNode(
        "8",
        "Log topical cluster plan to Sheets",
        1720,
        "Cluster_Plans",
        `[["${new Date().toISOString()}","{pillar_topic}","{total_clusters}","{total_content_pieces}","{new_content_gaps}","{total_briefs_created}","{total_word_count_estimate}","{total_publication_weeks}","{recommended_frequency}","{first_publish_title}"]]`,
      ),

      // 9. Team kick-off (Slack)
      slackNode(
        "9",
        "Slack: Kick off topical cluster project",
        1960,
        "Topical Cluster Plan Ready\\n===========================\\nPillar Topic: {pillar_topic}\\nTotal Clusters: {total_clusters}\\nContent Pieces: {total_content_pieces}\\nNew Gaps Found: {new_content_gaps} ({gap_percentage}% gap)\\nBriefs Created: {total_briefs_created}\\n\\nEstimated Word Count: {total_word_count_estimate}\\nPublication Timeline: {total_publication_weeks} weeks\\nFrequency: {recommended_frequency}\\nFirst to Publish: {first_publish_title}\\n\\nFull cluster plan, briefs, and schedule in Sheets.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 1093. COMPARISON PAGE FINDER (7 nodes)
  // ===================================================================

  "Comparison Page Finder": {
    summary:
      "Takes a category and competitor list, generates comparison queries with code, fetches search volume and rankings via SerpAPI, scores opportunities, generates AI comparison briefs, logs the comparison map to Sheets, and distributes to the content team via Slack.",
    nodes: [
      trigger("Webhook: Category and competitors submitted", "webhook_app", {
        source: "internal",
        event: "comparison_page_request",
        howItStarts:
          "A category name and competitor list are submitted to discover high-value comparison page opportunities based on search demand and competitive gaps",
        setup:
          "Configure SerpAPI for search volume and ranking data. Set up OpenAI for opportunity scoring and brief generation. Provide Sheets for the comparison map and Slack for team distribution.",
        inputFields: [],
      }),

      // 2. Comparison query generator (runCode)
      n("2", "runCode", "Generate comparison search queries", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Generate comparison queries from category and competitors
const payload = JSON.parse(vars.webhook_payload || "{}");
const category = payload.category || "software";
const competitors = (payload.competitors || "").split(",").map(c => c.trim()).filter(Boolean);
const brand = payload.your_brand || "our product";
const queries = [];
// vs queries
competitors.forEach(c => {
  queries.push(brand + " vs " + c);
  queries.push(c + " vs " + brand);
  queries.push(c + " alternatives");
  queries.push(c + " competitor");
  queries.push("best " + category + " " + c + " or " + brand);
});
// cross-competitor queries
for (let i = 0; i < competitors.length; i++) {
  for (let j = i + 1; j < competitors.length; j++) {
    queries.push(competitors[i] + " vs " + competitors[j]);
  }
}
// category queries
queries.push("best " + category + " comparison");
queries.push("top " + category + " compared");
queries.push(category + " comparison chart");
const unique = [...new Set(queries)];
return {
  comparison_queries_json: JSON.stringify(unique),
  total_queries: unique.length,
  category: category,
  brand: brand,
  competitor_count: competitors.length
};`,
          outputVariables: [
            { name: "comparison_queries_json", type: "string" },
            { name: "total_queries", type: "number" },
            { name: "category", type: "string" },
            { name: "brand", type: "string" },
            { name: "competitor_count", type: "number" },
          ],
        },
      }),

      // 3. SERP volume + ranking fetch (SerpAPI)
      n(
        "3",
        "hitApi",
        "SerpAPI: Fetch volume and rankings for comparison queries",
        520,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://serpapi.com/search.json?q={comparison_queries_json}&api_key={serpapi_key}&engine=google&gl=us&num=10",
            headers: [],
            body: [],
            variables: [
              {
                name: "organic_results",
                customName: "serp_comparison_results",
                type: "string",
              },
              {
                name: "search_information.total_results",
                customName: "comparison_total_results",
                type: "number",
              },
              {
                name: "related_searches",
                customName: "related_comparison_queries",
                type: "string",
              },
            ],
          },
        },
      ),

      // 4. Opportunity scorer (AI)
      aiNode(
        "4",
        "AI: Score comparison page opportunities",
        760,
        "You are a competitive content strategist. Score comparison page opportunities based on estimated search volume, competition level, conversion potential, and content effort required. Return JSON.",
        "Score these comparison page opportunities. Category: {category}. Brand: {brand}. Competitors: {competitor_count}. Queries generated: {total_queries}. SERP data: {serp_comparison_results}. Related searches: {related_comparison_queries}. For each viable opportunity, score opportunity_score (1-100), estimate monthly_volume, assess competition_level (low/medium/high), and rate conversion_potential (high/medium/low). Return scored_opportunities (array of {query, opportunity_score, monthly_volume, competition_level, conversion_potential, recommended_format}), total_viable (number), top_opportunity_query (string), estimated_total_monthly_volume (number).",
        [
          {
            field: "scored_opportunities",
            as: "scored_opportunities_json",
            type: "string",
          },
          {
            field: "total_viable",
            as: "total_viable_opportunities",
            type: "number",
          },
          {
            field: "top_opportunity_query",
            as: "top_opportunity_query",
            type: "string",
          },
          {
            field: "estimated_total_monthly_volume",
            as: "estimated_total_volume",
            type: "number",
          },
        ],
      ),

      // 5. AI comparison brief generator
      aiNode(
        "5",
        "AI: Generate comparison page content briefs",
        1000,
        "You are a comparison content specialist. Create detailed briefs for comparison pages that are fair, comprehensive, and optimized for conversion. Include comparison criteria, feature matrices, and recommended CTAs. Return JSON.",
        "Generate comparison page briefs for top opportunities: {scored_opportunities_json}. Brand: {brand}. Category: {category}. For each with opportunity_score > 50, create brief with comparison_title, h1_tag, comparison_criteria (array), feature_matrix_columns (array), word_count_target, primary_cta, seo_title, meta_description. Return comparison_briefs (array), total_briefs (number), estimated_total_words (number).",
        [
          {
            field: "comparison_briefs",
            as: "comparison_briefs_json",
            type: "string",
          },
          {
            field: "total_briefs",
            as: "total_comparison_briefs",
            type: "number",
          },
          {
            field: "estimated_total_words",
            as: "estimated_comparison_words",
            type: "number",
          },
        ],
      ),

      // 6. Comparison map report (Sheets)
      sheetsNode(
        "6",
        "Log comparison page map to Sheets",
        1240,
        "Comparison_Pages",
        `[["${new Date().toISOString()}","{category}","{brand}","{competitor_count}","{total_queries}","{total_viable_opportunities}","{top_opportunity_query}","{estimated_total_volume}","{total_comparison_briefs}","{estimated_comparison_words}"]]`,
      ),

      // 7. Content team distribution (Slack)
      slackNode(
        "7",
        "Slack: Distribute comparison page plan",
        1480,
        "Comparison Page Opportunity Map\\n================================\\nCategory: {category}\\nBrand: {brand}\\nCompetitors Analyzed: {competitor_count}\\nQueries Researched: {total_queries}\\n\\nViable Opportunities: {total_viable_opportunities}\\nTop Opportunity: {top_opportunity_query}\\nEstimated Monthly Volume: {estimated_total_volume}\\n\\nBriefs Created: {total_comparison_briefs}\\nEstimated Words: {estimated_comparison_words}\\n\\nFull opportunity map and briefs in Sheets.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1094. ALTERNATIVES PAGE PLANNER (7 nodes)
  // ===================================================================

  "Alternatives Page Planner": {
    summary:
      "Takes a competitor target, researches alternatives queries via SerpAPI, analyzes SERP competitors, generates AI alternatives page briefs, extracts differentiation angles, builds an alternatives plan report in Sheets, and hands off to the team via Slack.",
    nodes: [
      trigger(
        "Webhook: Competitor target for alternatives page",
        "webhook_app",
        {
          source: "internal",
          event: "alternatives_page_request",
          howItStarts:
            "A competitor brand name is submitted to research alternatives search demand and plan optimized alternatives pages that capture competitor defection traffic",
          setup:
            "Configure SerpAPI for alternatives keyword research. Set up OpenAI for brief generation and differentiation analysis. Provide Sheets for the plan and Slack for team handoff.",
          inputFields: [],
        },
      ),

      // 2. Alternatives query researcher (SerpAPI)
      n("2", "hitApi", "SerpAPI: Research alternatives queries", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://serpapi.com/search.json?q={competitor_name}+alternatives&api_key={serpapi_key}&engine=google&gl=us&num=20",
          headers: [],
          body: [],
          variables: [
            {
              name: "organic_results",
              customName: "alt_serp_results",
              type: "string",
            },
            {
              name: "related_searches",
              customName: "alt_related_searches",
              type: "string",
            },
            {
              name: "search_information.total_results",
              customName: "alt_total_results",
              type: "number",
            },
          ],
        },
      }),

      // 3. SERP competitor analyzer (AI)
      aiNode(
        "3",
        "AI: Analyze who ranks for alternatives queries",
        520,
        "You are an SEO competitive intelligence analyst. Analyze SERP results for alternatives queries to understand who currently ranks, what content formats dominate, and where the gaps exist. Return JSON.",
        "Analyze alternatives SERP for competitor: {competitor_name}. Organic results: {alt_serp_results}. Related searches: {alt_related_searches}. Total results: {alt_total_results}. Identify current_rankers (array of {domain, position, title, content_type}), dominant_format (string), content_gap_opportunities (array of strings), estimated_monthly_searches (number), difficulty_assessment (string), top_ranking_domain (string).",
        [
          {
            field: "current_rankers",
            as: "current_rankers_json",
            type: "string",
          },
          {
            field: "dominant_format",
            as: "dominant_alt_format",
            type: "string",
          },
          {
            field: "content_gap_opportunities",
            as: "content_gap_json",
            type: "string",
          },
          {
            field: "estimated_monthly_searches",
            as: "estimated_alt_searches",
            type: "number",
          },
          {
            field: "difficulty_assessment",
            as: "alt_difficulty",
            type: "string",
          },
          {
            field: "top_ranking_domain",
            as: "top_ranking_domain",
            type: "string",
          },
        ],
      ),

      // 4. AI alternatives brief generator
      aiNode(
        "4",
        "AI: Generate alternatives page content brief",
        760,
        "You are a conversion-focused SEO content strategist. Create alternatives page briefs that position your product favorably while being genuinely helpful and honest. Include comparison angles, social proof placement, and conversion paths. Return JSON.",
        "Create an alternatives page brief targeting {competitor_name} alternatives. Estimated searches: {estimated_alt_searches}/mo. Dominant format: {dominant_alt_format}. Content gaps: {content_gap_json}. Difficulty: {alt_difficulty}. Generate page_title, meta_description, h1_tag, outline (array of section headings), comparison_angles (array), social_proof_placements (array), primary_cta, secondary_cta, target_word_count, internal_link_suggestions (array). Return alternatives_brief (object with all fields), seo_title (string), estimated_ranking_timeline (string).",
        [
          {
            field: "alternatives_brief",
            as: "alternatives_brief_json",
            type: "string",
          },
          { field: "seo_title", as: "alt_seo_title", type: "string" },
          {
            field: "estimated_ranking_timeline",
            as: "ranking_timeline",
            type: "string",
          },
        ],
      ),

      // 5. Differentiation angle extractor
      aiNode(
        "5",
        "AI: Extract key differentiation angles",
        1000,
        "You are a competitive positioning expert. Identify the strongest differentiation angles between the user's product and the competitor to highlight in alternatives content. Return JSON.",
        "Extract differentiation angles for alternatives to {competitor_name}. Current rankers: {current_rankers_json}. Content gaps: {content_gap_json}. Brief: {alternatives_brief_json}. Identify the top differentiation_angles (array of {angle, strength, evidence_type, messaging_suggestion}), positioning_statement (string), key_weakness_of_competitor (string), key_strength_to_highlight (string), risk_factors (array of strings).",
        [
          {
            field: "differentiation_angles",
            as: "differentiation_angles_json",
            type: "string",
          },
          {
            field: "positioning_statement",
            as: "positioning_statement",
            type: "string",
          },
          {
            field: "key_weakness_of_competitor",
            as: "competitor_weakness",
            type: "string",
          },
          {
            field: "key_strength_to_highlight",
            as: "our_key_strength",
            type: "string",
          },
        ],
      ),

      // 6. Alternatives plan report (Sheets)
      sheetsNode(
        "6",
        "Log alternatives page plan to Sheets",
        1240,
        "Alternatives_Pages",
        `[["${new Date().toISOString()}","{competitor_name}","{estimated_alt_searches}","{alt_difficulty}","{dominant_alt_format}","{alt_seo_title}","{ranking_timeline}","{positioning_statement}","{competitor_weakness}","{our_key_strength}"]]`,
      ),

      // 7. Team handoff (Slack)
      slackNode(
        "7",
        "Slack: Hand off alternatives page plan",
        1480,
        "Alternatives Page Plan\\n=======================\\nTarget Competitor: {competitor_name}\\nEstimated Monthly Searches: {estimated_alt_searches}\\nDifficulty: {alt_difficulty}\\nDominant Format: {dominant_alt_format}\\n\\nSEO Title: {alt_seo_title}\\nRanking Timeline: {ranking_timeline}\\n\\nPositioning: {positioning_statement}\\nOur Strength: {our_key_strength}\\nCompetitor Weakness: {competitor_weakness}\\n\\nFull brief and differentiation angles in Sheets.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1095. GLOSSARY GENERATOR (8 nodes)
  // ===================================================================

  "Glossary Generator": {
    summary:
      "Takes industry and keyword inputs, discovers definition queries via SerpAPI, prioritizes terms with code, uses AI to write definitions, maps internal links, generates schema markup with code, builds glossary pages in Sheets, and hands off to publication via Slack.",
    nodes: [
      trigger("Webhook: Industry glossary generation request", "webhook_app", {
        source: "internal",
        event: "glossary_generation_request",
        howItStarts:
          "An industry category and seed keywords are submitted to generate a comprehensive SEO-optimized glossary with schema markup and internal linking",
        setup:
          "Configure SerpAPI for definition query discovery. Set up OpenAI for definition writing. Provide your site URL for internal link mapping. Set up Sheets for glossary output and Slack for publication handoff.",
        inputFields: [],
      }),

      // 2. Definition query discoverer (SerpAPI)
      n(
        "2",
        "hitApi",
        "SerpAPI: Discover definition queries in industry",
        280,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://serpapi.com/search.json?q=what+is+{industry_keyword}&api_key={serpapi_key}&engine=google&gl=us&num=20",
            headers: [],
            body: [],
            variables: [
              {
                name: "organic_results",
                customName: "definition_serp_results",
                type: "string",
              },
              {
                name: "related_searches",
                customName: "related_definitions",
                type: "string",
              },
              {
                name: "knowledge_graph",
                customName: "knowledge_graph_data",
                type: "string",
              },
            ],
          },
        },
      ),

      // 3. Term prioritizer (runCode)
      n("3", "runCode", "Prioritize glossary terms by search value", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Extract and prioritize glossary terms from SERP and related queries
const results = JSON.parse(vars.definition_serp_results || "[]");
const related = JSON.parse(vars.related_definitions || "[]");
const payload = JSON.parse(vars.webhook_payload || "{}");
const seedKeywords = (payload.seed_keywords || "").split(",").map(k => k.trim()).filter(Boolean);
const terms = new Set();
// Extract terms from related searches
related.forEach(r => {
  const q = (r.query || "").replace(/what is |definition of |meaning of /gi, "").trim();
  if (q.length > 2) terms.add(q);
});
// Extract from SERP titles
results.forEach(r => {
  const title = (r.title || "").replace(/- Definition|What is |Definition of |Meaning of |: A Guide.*/gi, "").trim();
  if (title.length > 2 && title.length < 60) terms.add(title);
});
// Add seed keywords
seedKeywords.forEach(k => terms.add(k));
const termList = Array.from(terms).map((t, i) => ({
  term: t,
  priority: i < seedKeywords.length ? "high" : i < seedKeywords.length + 10 ? "medium" : "low",
  has_featured_snippet: results.some(r => (r.title || "").toLowerCase().includes(t.toLowerCase()) && r.position <= 3)
}));
return {
  glossary_terms_json: JSON.stringify(termList),
  total_terms: termList.length,
  high_priority_terms: termList.filter(t => t.priority === "high").length,
  snippet_opportunity_terms: termList.filter(t => t.has_featured_snippet).length,
  industry: payload.industry || "general"
};`,
          outputVariables: [
            { name: "glossary_terms_json", type: "string" },
            { name: "total_terms", type: "number" },
            { name: "high_priority_terms", type: "number" },
            { name: "snippet_opportunity_terms", type: "number" },
            { name: "industry", type: "string" },
          ],
        },
      }),

      // 4. AI definition writer
      aiNode(
        "4",
        "AI: Write glossary definitions",
        760,
        "You are a technical writer specializing in clear, authoritative glossary definitions. Write definitions that are concise (2-3 sentences for the summary, 1-2 paragraphs for the full definition), accurate, and optimized for featured snippets. Use industry-standard terminology. Return JSON.",
        "Write glossary definitions for these {industry} terms: {glossary_terms_json}. Total terms: {total_terms}. For each term, write definition_summary (2-3 sentences, featured-snippet optimized), full_definition (1-2 paragraphs with examples), related_terms (array of 3-5 related glossary terms), usage_example (one sentence using the term in context). Return definitions (array of {term, definition_summary, full_definition, related_terms, usage_example}), total_definitions (number).",
        [
          { field: "definitions", as: "definitions_json", type: "string" },
          {
            field: "total_definitions",
            as: "total_definitions",
            type: "number",
          },
        ],
      ),

      // 5. Internal link mapper
      aiNode(
        "5",
        "AI: Map internal links for glossary terms",
        1000,
        "You are an internal linking strategist. For each glossary term, identify the most relevant existing site pages to link to and from. Ensure each definition connects to related content on the site. Return JSON.",
        "Map internal links for these glossary definitions: {definitions_json}. Industry: {industry}. For each term, suggest link_to_pages (array of suggested URLs/paths on the site where this term should link TO), link_from_pages (array of pages that should link TO this glossary entry), anchor_text_variations (array of 2-3 anchor text options). Return link_map (array of {term, link_to_pages, link_from_pages, anchor_text_variations}), total_internal_links_suggested (number), cross_reference_count (number).",
        [
          { field: "link_map", as: "glossary_link_map_json", type: "string" },
          {
            field: "total_internal_links_suggested",
            as: "total_internal_links",
            type: "number",
          },
          {
            field: "cross_reference_count",
            as: "cross_reference_count",
            type: "number",
          },
        ],
      ),

      // 6. Schema markup generator (runCode)
      n(
        "6",
        "runCode",
        "Generate DefinedTerm schema markup for glossary",
        1240,
        {
          runCodeDetails: {
            language: "javascript",
            code: `// Generate JSON-LD DefinedTerm schema for each glossary entry
const definitions = JSON.parse(vars.definitions_json || "[]");
const industry = vars.industry || "general";
const schemas = definitions.map(d => ({
  "@context": "https://schema.org",
  "@type": "DefinedTerm",
  "name": d.term,
  "description": d.definition_summary,
  "inDefinedTermSet": {
    "@type": "DefinedTermSet",
    "name": industry + " Glossary"
  }
}));
const fullSchema = {
  "@context": "https://schema.org",
  "@type": "DefinedTermSet",
  "name": industry + " Glossary",
  "description": "Comprehensive glossary of " + industry + " terms and definitions",
  "hasDefinedTerm": schemas
};
return {
  glossary_schema_json: JSON.stringify(fullSchema),
  individual_schemas_json: JSON.stringify(schemas),
  schema_count: schemas.length,
  schema_valid: schemas.length > 0 ? "true" : "false"
};`,
            outputVariables: [
              { name: "glossary_schema_json", type: "string" },
              { name: "individual_schemas_json", type: "string" },
              { name: "schema_count", type: "number" },
              { name: "schema_valid", type: "string" },
            ],
          },
        },
      ),

      // 7. Glossary page builder (Sheets)
      sheetsNode(
        "7",
        "Log glossary content to Sheets",
        1480,
        "Glossary",
        `[["${new Date().toISOString()}","{industry}","{total_terms}","{total_definitions}","{high_priority_terms}","{snippet_opportunity_terms}","{total_internal_links}","{cross_reference_count}","{schema_count}"]]`,
      ),

      // 8. Publication handoff (Slack)
      slackNode(
        "8",
        "Slack: Hand off glossary for publication",
        1720,
        "Glossary Generation Complete\\n==============================\\nIndustry: {industry}\\nTerms Discovered: {total_terms}\\nDefinitions Written: {total_definitions}\\nHigh Priority: {high_priority_terms}\\nSnippet Opportunities: {snippet_opportunity_terms}\\n\\nInternal Links Mapped: {total_internal_links}\\nCross-References: {cross_reference_count}\\nSchema Entries: {schema_count}\\n\\nAll definitions, schema markup, and internal linking map in Sheets.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1096. PROGRAMMATIC SEO MAP (9 nodes)
  // ===================================================================

  "Programmatic SEO Map": {
    summary:
      "Analyzes the business model to discover template patterns via SerpAPI, identifies data sources, uses AI to design page templates, estimates scale with code, plans quality gates, builds an implementation roadmap, logs the blueprint to Sheets, and presents to stakeholders via email.",
    nodes: [
      trigger(
        "Webhook: Business model submitted for programmatic SEO",
        "webhook_app",
        {
          source: "internal",
          event: "programmatic_seo_request",
          howItStarts:
            "A business model description and target market are submitted to design a comprehensive programmatic SEO strategy with scalable page templates",
          setup:
            "Configure SerpAPI for template pattern discovery. Set up OpenAI for template design and roadmap planning. Provide Sheets for the blueprint and an email address for stakeholder presentations.",
          inputFields: [],
        },
      ),

      // 2. Template pattern discovery (SerpAPI)
      n(
        "2",
        "hitApi",
        "SerpAPI: Discover programmatic template patterns",
        280,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://serpapi.com/search.json?q={business_category}+in+{target_location}&api_key={serpapi_key}&engine=google&gl=us&num=20",
            headers: [],
            body: [],
            variables: [
              {
                name: "organic_results",
                customName: "template_serp_results",
                type: "string",
              },
              {
                name: "related_searches",
                customName: "template_related_queries",
                type: "string",
              },
              {
                name: "search_information.total_results",
                customName: "template_total_results",
                type: "number",
              },
            ],
          },
        },
      ),

      // 3. Data source identifier (AI)
      aiNode(
        "3",
        "AI: Identify available data sources for page generation",
        520,
        "You are a programmatic SEO data strategist. Identify the best data sources (APIs, databases, public datasets) that can power scalable page generation. Evaluate data quality, freshness, and legal considerations. Return JSON.",
        "Identify data sources for programmatic SEO. Business model from webhook: {webhook_payload}. SERP patterns: {template_serp_results}. Related queries: {template_related_queries}. Suggest data_sources (array of {source_name, source_type, data_format, update_frequency, legal_status, quality_score}), primary_data_source (string), data_enrichment_options (array of strings), estimated_unique_pages_possible (number), data_freshness_strategy (string).",
        [
          { field: "data_sources", as: "data_sources_json", type: "string" },
          {
            field: "primary_data_source",
            as: "primary_data_source",
            type: "string",
          },
          {
            field: "estimated_unique_pages_possible",
            as: "estimated_pages",
            type: "number",
          },
          {
            field: "data_freshness_strategy",
            as: "data_freshness_strategy",
            type: "string",
          },
          {
            field: "data_enrichment_options",
            as: "enrichment_options_json",
            type: "string",
          },
        ],
      ),

      // 4. AI template designer
      aiNode(
        "4",
        "AI: Design programmatic page templates",
        760,
        "You are a programmatic SEO template architect. Design page templates that generate unique, valuable content at scale while avoiding thin content penalties. Include dynamic content zones, UGC integration points, and internal linking patterns. Return JSON.",
        "Design page templates for programmatic SEO. Estimated pages: {estimated_pages}. Primary data source: {primary_data_source}. SERP patterns: {template_serp_results}. Enrichment options: {enrichment_options_json}. Design templates with template_name, url_pattern, h1_pattern, dynamic_sections (array of {section_name, data_source, content_type}), unique_value_proposition, internal_link_pattern, schema_type. Return templates (array), total_templates (number), estimated_avg_word_count (number), uniqueness_strategy (string).",
        [
          { field: "templates", as: "page_templates_json", type: "string" },
          { field: "total_templates", as: "total_templates", type: "number" },
          {
            field: "estimated_avg_word_count",
            as: "avg_word_count",
            type: "number",
          },
          {
            field: "uniqueness_strategy",
            as: "uniqueness_strategy",
            type: "string",
          },
        ],
      ),

      // 5. Scale estimator (runCode)
      n(
        "5",
        "runCode",
        "Estimate programmatic page scale and resources",
        1000,
        {
          runCodeDetails: {
            language: "javascript",
            code: `// Estimate the scale, cost, and resources needed for programmatic SEO
const templates = JSON.parse(vars.page_templates_json || "[]");
const estimatedPages = parseInt(vars.estimated_pages) || 0;
const avgWordCount = parseInt(vars.avg_word_count) || 500;
const pagesPerTemplate = Math.ceil(estimatedPages / Math.max(templates.length, 1));
const totalWords = estimatedPages * avgWordCount;
const estimatedCrawlBudget = Math.ceil(estimatedPages / 1000) * 100;
const estimatedStorageMB = Math.ceil((totalWords * 6) / 1048576);
const phases = [
  { phase: 1, pages: Math.min(100, estimatedPages), description: "Pilot batch - validate template quality" },
  { phase: 2, pages: Math.min(1000, estimatedPages), description: "Scale to 1K - monitor indexing rate" },
  { phase: 3, pages: estimatedPages, description: "Full scale - all pages live" }
];
return {
  scale_estimate_json: JSON.stringify({ estimatedPages, pagesPerTemplate, totalWords, estimatedCrawlBudget, estimatedStorageMB, phases }),
  total_estimated_pages: estimatedPages,
  pages_per_template: pagesPerTemplate,
  total_estimated_words: totalWords,
  crawl_budget_impact: estimatedCrawlBudget,
  deployment_phases_json: JSON.stringify(phases)
};`,
            outputVariables: [
              { name: "scale_estimate_json", type: "string" },
              { name: "total_estimated_pages", type: "number" },
              { name: "pages_per_template", type: "number" },
              { name: "total_estimated_words", type: "number" },
              { name: "crawl_budget_impact", type: "number" },
              { name: "deployment_phases_json", type: "string" },
            ],
          },
        },
      ),

      // 6. Quality gate planner (AI)
      aiNode(
        "6",
        "AI: Plan quality gates for programmatic pages",
        1240,
        "You are a programmatic SEO quality assurance expert. Design quality gates that prevent thin content, duplicate content, and indexing issues at scale. Include automated checks and manual review thresholds. Return JSON.",
        "Plan quality gates for {total_estimated_pages} programmatic pages. Templates: {page_templates_json}. Uniqueness strategy: {uniqueness_strategy}. Phases: {deployment_phases_json}. Design quality_gates (array of {gate_name, trigger_condition, check_type, pass_criteria, failure_action}), automated_checks (array of strings), manual_review_thresholds (object), indexing_strategy (string), thin_content_prevention (string).",
        [
          { field: "quality_gates", as: "quality_gates_json", type: "string" },
          {
            field: "indexing_strategy",
            as: "indexing_strategy",
            type: "string",
          },
          {
            field: "thin_content_prevention",
            as: "thin_content_prevention",
            type: "string",
          },
          {
            field: "manual_review_thresholds",
            as: "review_thresholds_json",
            type: "string",
          },
        ],
      ),

      // 7. Implementation roadmap (AI)
      aiNode(
        "7",
        "AI: Build implementation roadmap",
        1480,
        "You are a technical project manager specializing in programmatic SEO implementations. Create a detailed, phase-gated roadmap with milestones, team assignments, and success metrics. Return JSON.",
        "Build implementation roadmap for programmatic SEO. Pages: {total_estimated_pages}. Templates: {total_templates}. Phases: {deployment_phases_json}. Quality gates: {quality_gates_json}. Indexing strategy: {indexing_strategy}. Create roadmap_phases (array of {phase, duration_weeks, milestones, team_required, success_metrics, risk_factors}), total_timeline_weeks (number), launch_prerequisites (array), first_milestone (string), estimated_traffic_at_full_scale (string).",
        [
          { field: "roadmap_phases", as: "roadmap_json", type: "string" },
          {
            field: "total_timeline_weeks",
            as: "total_timeline_weeks",
            type: "number",
          },
          { field: "first_milestone", as: "first_milestone", type: "string" },
          {
            field: "estimated_traffic_at_full_scale",
            as: "estimated_full_traffic",
            type: "string",
          },
          {
            field: "launch_prerequisites",
            as: "launch_prerequisites_json",
            type: "string",
          },
        ],
      ),

      // 8. Programmatic SEO blueprint (Sheets)
      sheetsNode(
        "8",
        "Log programmatic SEO blueprint to Sheets",
        1720,
        "Programmatic_SEO",
        `[["${new Date().toISOString()}","{primary_data_source}","{total_templates}","{total_estimated_pages}","{avg_word_count}","{uniqueness_strategy}","{indexing_strategy}","{total_timeline_weeks}","{first_milestone}","{estimated_full_traffic}"]]`,
      ),

      // 9. Stakeholder presentation (email)
      emailNode(
        "9",
        "Email: Present programmatic SEO blueprint to stakeholders",
        1960,
        "{stakeholder_email}",
        "Programmatic SEO Blueprint: {total_estimated_pages} Pages | {total_templates} Templates",
        "Programmatic SEO Blueprint\\n\\nEstimated Pages: {total_estimated_pages}\\nTemplates: {total_templates}\\nData Source: {primary_data_source}\\nTimeline: {total_timeline_weeks} weeks\\n\\nFirst Milestone: {first_milestone}\\nEstimated Traffic at Scale: {estimated_full_traffic}\\n\\nUniqueness Strategy: {uniqueness_strategy}\\nIndexing Strategy: {indexing_strategy}\\nThin Content Prevention: {thin_content_prevention}\\n\\nThe full blueprint with templates, quality gates, and roadmap is available in the shared Sheets document.\\n\\nNext Steps:\\n1. Review the data source feasibility\\n2. Approve template designs\\n3. Greenlight Phase 1 pilot (100 pages)",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 1097. FAQ CLUSTER BUILDER (8 nodes)
  // ===================================================================

  "FAQ Cluster Builder": {
    summary:
      "Aggregates questions from multiple sources, deduplicates and normalizes with code, clusters by topic via AI, enriches with search volume from SerpAPI, generates AI answers, formats FAQ schema with code, packages content in Sheets, and delivers to the content team via Slack.",
    nodes: [
      trigger("Schedule: Multi-source question aggregation", "schedule", {
        schedule: "0 6 * * 3 (every Wednesday at 6 AM)",
        howItStarts:
          "A scheduled job aggregates questions from support tickets, community forums, Google Search Console, and chatbot logs to build comprehensive FAQ clusters",
        setup:
          "Connect support platform APIs for question sourcing. Configure SerpAPI for volume enrichment. Set up OpenAI for clustering and answer generation. Provide Sheets for FAQ package output and Slack for delivery.",
        inputFields: [],
      }),

      // 2. Question dedup + normalizer (runCode)
      n("2", "runCode", "Deduplicate and normalize questions", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Deduplicate and normalize questions from multiple sources
const payload = JSON.parse(vars.webhook_payload || "{}");
const sources = {
  support: payload.support_questions || [],
  community: payload.community_questions || [],
  search_console: payload.search_queries || [],
  chatbot: payload.chatbot_questions || []
};
const allQuestions = [];
Object.entries(sources).forEach(([source, questions]) => {
  (Array.isArray(questions) ? questions : []).forEach(q => {
    const text = typeof q === "string" ? q : q.question || q.query || q.text || "";
    if (text.length > 10) allQuestions.push({ text: text.trim(), source });
  });
});
const normalized = {};
allQuestions.forEach(q => {
  const key = q.text.toLowerCase().replace(/[^a-z0-9 ]/g, "").replace(/\\s+/g, " ").trim().substring(0, 100);
  if (!normalized[key]) normalized[key] = { question: q.text, sources: [], count: 0 };
  normalized[key].sources.push(q.source);
  normalized[key].count++;
});
const unique = Object.values(normalized).sort((a, b) => b.count - a.count);
return {
  normalized_questions_json: JSON.stringify(unique.slice(0, 60)),
  total_raw_questions: allQuestions.length,
  total_unique_questions: unique.length,
  dedup_rate: allQuestions.length > 0 ? Math.round(((allQuestions.length - unique.length) / allQuestions.length) * 100) : 0,
  top_question: unique.length > 0 ? unique[0].question : "none"
};`,
          outputVariables: [
            { name: "normalized_questions_json", type: "string" },
            { name: "total_raw_questions", type: "number" },
            { name: "total_unique_questions", type: "number" },
            { name: "dedup_rate", type: "number" },
            { name: "top_question", type: "string" },
          ],
        },
      }),

      // 3. AI topic clusterer
      aiNode(
        "3",
        "AI: Cluster questions by topic",
        520,
        "You are a content organization expert. Group related questions into topical clusters that map to logical FAQ page sections or standalone FAQ pages. Identify parent topics and sub-questions. Return JSON.",
        "Cluster these {total_unique_questions} unique questions into topical groups: {normalized_questions_json}. Group by theme, identifying cluster_name, parent_topic, and questions within each cluster. Return clusters (array of {cluster_name, parent_topic, questions (array of strings), question_count, recommended_page_type}), total_clusters (number), largest_cluster_name (string), largest_cluster_count (number).",
        [
          { field: "clusters", as: "question_clusters_json", type: "string" },
          {
            field: "total_clusters",
            as: "total_question_clusters",
            type: "number",
          },
          {
            field: "largest_cluster_name",
            as: "largest_cluster_name",
            type: "string",
          },
          {
            field: "largest_cluster_count",
            as: "largest_cluster_count",
            type: "number",
          },
        ],
      ),

      // 4. Volume + intent enricher (SerpAPI)
      n("4", "hitApi", "SerpAPI: Enrich FAQ clusters with search volume", 760, {
        hitApiDetails: {
          method: "GET",
          url: "https://serpapi.com/search.json?q={top_question}&api_key={serpapi_key}&engine=google&gl=us&num=10",
          headers: [],
          body: [],
          variables: [
            {
              name: "organic_results",
              customName: "faq_serp_results",
              type: "string",
            },
            {
              name: "related_searches",
              customName: "faq_related_queries",
              type: "string",
            },
            {
              name: "search_information.total_results",
              customName: "faq_search_total",
              type: "number",
            },
          ],
        },
      }),

      // 5. AI answer generator
      aiNode(
        "5",
        "AI: Generate comprehensive FAQ answers",
        1000,
        "You are a knowledgeable FAQ content writer. Write clear, comprehensive answers that are optimized for featured snippets. Start each answer with a direct response, then elaborate with details and examples. Return JSON.",
        "Generate answers for these FAQ clusters: {question_clusters_json}. SERP context: {faq_serp_results}. Related queries: {faq_related_queries}. For each question in each cluster, write short_answer (1-2 sentences, snippet-optimized), detailed_answer (2-3 paragraphs), and related_link_suggestion (path). Return answered_clusters (array of {cluster_name, faqs (array of {question, short_answer, detailed_answer, related_link_suggestion})}), total_answers_written (number).",
        [
          {
            field: "answered_clusters",
            as: "answered_clusters_json",
            type: "string",
          },
          {
            field: "total_answers_written",
            as: "total_answers_written",
            type: "number",
          },
        ],
      ),

      // 6. FAQ schema formatter (runCode)
      n(
        "6",
        "runCode",
        "Generate FAQPage schema markup for all clusters",
        1240,
        {
          runCodeDetails: {
            language: "javascript",
            code: `// Generate FAQPage JSON-LD schema for each cluster
const clusters = JSON.parse(vars.answered_clusters_json || "[]");
const schemas = clusters.map(c => {
  const faqs = (c.faqs || []).map(f => ({
    "@type": "Question",
    "name": f.question,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": f.short_answer + " " + f.detailed_answer
    }
  }));
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "name": c.cluster_name + " FAQ",
    "mainEntity": faqs
  };
});
const totalQuestions = clusters.reduce((sum, c) => sum + (c.faqs || []).length, 0);
return {
  faq_schemas_json: JSON.stringify(schemas),
  total_schema_pages: schemas.length,
  total_schema_questions: totalQuestions,
  schema_generation_status: schemas.length > 0 ? "success" : "empty"
};`,
            outputVariables: [
              { name: "faq_schemas_json", type: "string" },
              { name: "total_schema_pages", type: "number" },
              { name: "total_schema_questions", type: "number" },
              { name: "schema_generation_status", type: "string" },
            ],
          },
        },
      ),

      // 7. FAQ content package (Sheets)
      sheetsNode(
        "7",
        "Log FAQ content package to Sheets",
        1480,
        "FAQ_Clusters",
        `[["${new Date().toISOString()}","{total_raw_questions}","{total_unique_questions}","{dedup_rate}%","{total_question_clusters}","{largest_cluster_name}","{total_answers_written}","{total_schema_pages}","{total_schema_questions}"]]`,
      ),

      // 8. Content team delivery (Slack)
      slackNode(
        "8",
        "Slack: Deliver FAQ content package to team",
        1720,
        "FAQ Cluster Package Ready\\n==========================\\nRaw Questions: {total_raw_questions}\\nUnique After Dedup: {total_unique_questions} ({dedup_rate}% duplicates)\\nClusters Created: {total_question_clusters}\\nLargest Cluster: {largest_cluster_name} ({largest_cluster_count} questions)\\n\\nAnswers Written: {total_answers_written}\\nFAQ Schema Pages: {total_schema_pages}\\nSchema Questions: {total_schema_questions}\\n\\nFull FAQ content, answers, and schema markup in Sheets.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1098. LOCAL SEO MAP (8 nodes)
  // ===================================================================

  "Local SEO Map": {
    summary:
      "Takes a service area trigger, analyzes local SERPs via SerpAPI, researches local keywords, maps local competitors, uses AI to plan geo-targeted content, generates local schema with code, logs the local SEO map to Sheets, and distributes to the team via Slack.",
    nodes: [
      trigger(
        "Webhook: Service area submitted for local SEO planning",
        "webhook_app",
        {
          source: "internal",
          event: "local_seo_request",
          howItStarts:
            "A service area (city, region, or zip codes) and business type are submitted to build a comprehensive local SEO content and schema strategy",
          setup:
            "Configure SerpAPI for local SERP analysis. Set up OpenAI for local content planning. Provide business details for local schema generation. Set up Sheets for the map and Slack for team distribution.",
          inputFields: [],
        },
      ),

      // 2. Local SERP analyzer (SerpAPI)
      n("2", "hitApi", "SerpAPI: Analyze local SERPs for service area", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://serpapi.com/search.json?q={business_type}+in+{service_area}&api_key={serpapi_key}&engine=google&gl=us&location={service_area}&num=20",
          headers: [],
          body: [],
          variables: [
            {
              name: "organic_results",
              customName: "local_organic_results",
              type: "string",
            },
            {
              name: "local_results",
              customName: "local_pack_results",
              type: "string",
            },
            {
              name: "related_searches",
              customName: "local_related_queries",
              type: "string",
            },
            {
              name: "search_information.total_results",
              customName: "local_total_results",
              type: "number",
            },
          ],
        },
      }),

      // 3. Local keyword researcher (AI)
      aiNode(
        "3",
        "AI: Research local keyword opportunities",
        520,
        "You are a local SEO keyword researcher. Identify high-value local keywords including geo-modified terms, near me queries, neighborhood-specific phrases, and service+location combinations. Return JSON.",
        "Research local keywords for {business_type} in {service_area}. Local SERP organic results: {local_organic_results}. Local pack: {local_pack_results}. Related queries: {local_related_queries}. Generate local_keywords (array of {keyword, intent, estimated_volume, competition, geo_modifier_type}), total_keywords (number), near_me_opportunities (number), service_area_pages_needed (number), local_content_themes (array of strings).",
        [
          {
            field: "local_keywords",
            as: "local_keywords_json",
            type: "string",
          },
          {
            field: "total_keywords",
            as: "total_local_keywords",
            type: "number",
          },
          {
            field: "near_me_opportunities",
            as: "near_me_count",
            type: "number",
          },
          {
            field: "service_area_pages_needed",
            as: "service_area_pages_needed",
            type: "number",
          },
          {
            field: "local_content_themes",
            as: "local_themes_json",
            type: "string",
          },
        ],
      ),

      // 4. Local competitor mapper (AI)
      aiNode(
        "4",
        "AI: Map local competitors and their strategies",
        760,
        "You are a local competitive intelligence analyst. Map local competitors, their SEO strategies, content approaches, and identify gaps you can exploit. Return JSON.",
        "Map local competitors for {business_type} in {service_area}. Local pack results: {local_pack_results}. Organic results: {local_organic_results}. For each competitor in local pack and top organic, identify competitor_name, website, local_pack_position, organic_position, has_service_area_pages, has_reviews, review_count, content_approach. Return competitors (array), total_competitors (number), top_competitor (string), competitive_gap (string), recommended_strategy (string).",
        [
          {
            field: "competitors",
            as: "local_competitors_json",
            type: "string",
          },
          {
            field: "total_competitors",
            as: "total_local_competitors",
            type: "number",
          },
          {
            field: "top_competitor",
            as: "top_local_competitor",
            type: "string",
          },
          {
            field: "competitive_gap",
            as: "local_competitive_gap",
            type: "string",
          },
          {
            field: "recommended_strategy",
            as: "local_seo_strategy",
            type: "string",
          },
        ],
      ),

      // 5. AI geo-targeted content planner
      aiNode(
        "5",
        "AI: Plan geo-targeted content for service area",
        1000,
        "You are a local content strategist. Plan geo-targeted landing pages, service area pages, and local blog content that establishes topical and geographic authority. Return JSON.",
        "Plan geo-targeted content for {business_type} in {service_area}. Local keywords: {local_keywords_json}. Themes: {local_themes_json}. Service area pages needed: {service_area_pages_needed}. Competitor strategy: {local_seo_strategy}. Plan service_area_pages (array of {page_title, target_keyword, target_location, content_type, word_count}), local_blog_topics (array of {title, keyword, local_angle}), total_pages_planned (number), estimated_monthly_traffic (number), implementation_priority (array of page titles).",
        [
          {
            field: "service_area_pages",
            as: "service_area_pages_json",
            type: "string",
          },
          {
            field: "local_blog_topics",
            as: "local_blog_topics_json",
            type: "string",
          },
          {
            field: "total_pages_planned",
            as: "total_local_pages_planned",
            type: "number",
          },
          {
            field: "estimated_monthly_traffic",
            as: "estimated_local_traffic",
            type: "number",
          },
          {
            field: "implementation_priority",
            as: "local_implementation_priority_json",
            type: "string",
          },
        ],
      ),

      // 6. Local schema generator (runCode)
      n(
        "6",
        "runCode",
        "Generate LocalBusiness schema for service area",
        1240,
        {
          runCodeDetails: {
            language: "javascript",
            code: `// Generate LocalBusiness and ServiceArea JSON-LD schema
const payload = JSON.parse(vars.webhook_payload || "{}");
const businessType = payload.business_type || "LocalBusiness";
const businessName = payload.business_name || "{company_name}";
const serviceArea = payload.service_area || "";
const address = payload.address || {};
const localSchema = {
  "@context": "https://schema.org",
  "@type": businessType,
  "name": businessName,
  "address": {
    "@type": "PostalAddress",
    "streetAddress": address.street || "",
    "addressLocality": address.city || serviceArea,
    "addressRegion": address.state || "",
    "postalCode": address.zip || "",
    "addressCountry": "US"
  },
  "areaServed": {
    "@type": "GeoCircle",
    "geoMidpoint": { "@type": "GeoCoordinates", "latitude": address.lat || "", "longitude": address.lng || "" },
    "geoRadius": payload.service_radius || "50 miles"
  },
  "url": payload.website || "",
  "telephone": payload.phone || ""
};
const servicePages = JSON.parse(vars.service_area_pages_json || "[]");
const pageSchemas = servicePages.map(p => ({
  "@context": "https://schema.org",
  "@type": "WebPage",
  "name": p.page_title,
  "about": { "@type": "Service", "name": p.page_title, "areaServed": p.target_location },
  "isPartOf": { "@type": "WebSite", "name": businessName }
}));
return {
  local_business_schema: JSON.stringify(localSchema),
  page_schemas_json: JSON.stringify(pageSchemas),
  total_schemas_generated: pageSchemas.length + 1,
  schema_status: "generated"
};`,
            outputVariables: [
              { name: "local_business_schema", type: "string" },
              { name: "page_schemas_json", type: "string" },
              { name: "total_schemas_generated", type: "number" },
              { name: "schema_status", type: "string" },
            ],
          },
        },
      ),

      // 7. Local SEO map report (Sheets)
      sheetsNode(
        "7",
        "Log local SEO map to Sheets",
        1480,
        "Local_SEO",
        `[["${new Date().toISOString()}","{service_area}","{business_type}","{total_local_keywords}","{near_me_count}","{total_local_competitors}","{top_local_competitor}","{total_local_pages_planned}","{estimated_local_traffic}","{total_schemas_generated}"]]`,
      ),

      // 8. Team distribution (Slack)
      slackNode(
        "8",
        "Slack: Distribute local SEO plan to team",
        1720,
        "Local SEO Map Complete\\n========================\\nService Area: {service_area}\\nBusiness Type: {business_type}\\n\\nKeywords Found: {total_local_keywords}\\nNear-Me Opportunities: {near_me_count}\\nLocal Competitors: {total_local_competitors}\\nTop Competitor: {top_local_competitor}\\n\\nPages Planned: {total_local_pages_planned}\\nEstimated Monthly Traffic: {estimated_local_traffic}\\nSchemas Generated: {total_schemas_generated}\\n\\nStrategy: {local_seo_strategy}\\nCompetitive Gap: {local_competitive_gap}\\n\\nFull local SEO map, content plan, and schema in Sheets.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1099. QUERY-TO-BRIEF GENERATOR (8 nodes)
  // ===================================================================

  "Query-to-Brief Generator": {
    summary:
      "Takes a target keyword, performs deep SERP analysis via SerpAPI, profiles competitor content, interprets search intent with AI, spots content gaps, composes a detailed AI brief, validates quality with code, and delivers the brief to Sheets.",
    nodes: [
      trigger(
        "Webhook: Target keyword submitted for brief generation",
        "webhook_app",
        {
          source: "internal",
          event: "query_to_brief_request",
          howItStarts:
            "A target keyword is submitted to generate a comprehensive, data-driven content brief based on deep SERP analysis and competitor profiling",
          setup:
            "Configure SerpAPI for SERP analysis. Set up OpenAI for intent analysis and brief composition. Provide Sheets for brief delivery.",
          inputFields: [],
        },
      ),

      // 2. SERP deep analyzer (SerpAPI)
      n("2", "hitApi", "SerpAPI: Deep SERP analysis for target keyword", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://serpapi.com/search.json?q={target_keyword}&api_key={serpapi_key}&engine=google&gl=us&num=20",
          headers: [],
          body: [],
          variables: [
            {
              name: "organic_results",
              customName: "serp_organic_json",
              type: "string",
            },
            {
              name: "related_searches",
              customName: "serp_related_json",
              type: "string",
            },
            {
              name: "knowledge_graph",
              customName: "serp_knowledge_graph",
              type: "string",
            },
            {
              name: "search_information.total_results",
              customName: "serp_total_results",
              type: "number",
            },
          ],
        },
      }),

      // 3. Competitor content profiler (AI)
      aiNode(
        "3",
        "AI: Profile top-ranking competitor content",
        520,
        "You are an SEO content analyst. Profile the top-ranking content for a keyword to understand what Google rewards. Analyze content structure, depth, media usage, word count patterns, and unique value propositions. Return JSON.",
        "Profile competitor content for keyword from webhook: {webhook_payload}. Top organic results: {serp_organic_json}. Knowledge graph: {serp_knowledge_graph}. For each top-10 result, profile title, estimated_word_count, content_structure (number of H2s), has_images, has_video, has_tables, unique_angle. Return competitor_profiles (array), avg_word_count (number), common_topics (array of strings), content_gaps (array of strings), dominant_content_type (string).",
        [
          {
            field: "competitor_profiles",
            as: "competitor_profiles_json",
            type: "string",
          },
          {
            field: "avg_word_count",
            as: "avg_competitor_word_count",
            type: "number",
          },
          { field: "common_topics", as: "common_topics_json", type: "string" },
          { field: "content_gaps", as: "content_gaps_json", type: "string" },
          {
            field: "dominant_content_type",
            as: "dominant_content_type",
            type: "string",
          },
        ],
      ),

      // 4. AI search intent interpreter
      aiNode(
        "4",
        "AI: Interpret search intent for keyword",
        760,
        "You are a search intent specialist. Analyze SERP features, result types, and user behavior signals to determine the precise search intent and content expectations for a query. Return JSON.",
        "Interpret search intent for keyword from webhook: {webhook_payload}. SERP results: {serp_organic_json}. Related searches: {serp_related_json}. Knowledge graph: {serp_knowledge_graph}. Determine primary_intent (informational/navigational/commercial/transactional), secondary_intent (if any), user_expectations (array of what users expect to find), content_format_expected (guide/listicle/comparison/tool/etc.), depth_expected (basic/intermediate/expert), freshness_importance (high/medium/low).",
        [
          {
            field: "primary_intent",
            as: "primary_search_intent",
            type: "string",
          },
          { field: "secondary_intent", as: "secondary_intent", type: "string" },
          {
            field: "user_expectations",
            as: "user_expectations_json",
            type: "string",
          },
          {
            field: "content_format_expected",
            as: "expected_format",
            type: "string",
          },
          { field: "depth_expected", as: "expected_depth", type: "string" },
          {
            field: "freshness_importance",
            as: "freshness_importance",
            type: "string",
          },
        ],
      ),

      // 5. Content gap spotter (AI)
      aiNode(
        "5",
        "AI: Identify content gaps to exploit",
        1000,
        "You are a content gap analyst. Compare what current top-ranking pages cover against what users actually need. Identify uncovered subtopics, missing perspectives, and opportunities to provide unique value. Return JSON.",
        "Identify content gaps for keyword from webhook: {webhook_payload}. Common topics covered: {common_topics_json}. Known gaps: {content_gaps_json}. User expectations: {user_expectations_json}. Search intent: {primary_search_intent}. Related queries: {serp_related_json}. Find specific uncovered_subtopics (array), missing_data_points (array), unique_angles (array of {angle, rationale}), differentiation_opportunities (array of strings), recommended_sections_to_add (array of H2 headings not in competitors).",
        [
          {
            field: "uncovered_subtopics",
            as: "uncovered_subtopics_json",
            type: "string",
          },
          { field: "unique_angles", as: "unique_angles_json", type: "string" },
          {
            field: "differentiation_opportunities",
            as: "differentiation_json",
            type: "string",
          },
          {
            field: "recommended_sections_to_add",
            as: "recommended_sections_json",
            type: "string",
          },
        ],
      ),

      // 6. AI brief composer
      aiNode(
        "6",
        "AI: Compose comprehensive content brief",
        1240,
        "You are an expert SEO content strategist. Compose a detailed, actionable content brief that a writer can follow to create the best-ranking content for a keyword. Include everything: title, meta, outline, word count, tone, sources, CTAs, and internal links. Return JSON.",
        "Compose a content brief for keyword from webhook: {webhook_payload}. Intent: {primary_search_intent}. Format: {expected_format}. Depth: {expected_depth}. Avg competitor word count: {avg_competitor_word_count}. Uncovered subtopics: {uncovered_subtopics_json}. Unique angles: {unique_angles_json}. Sections to add: {recommended_sections_json}. Differentiation: {differentiation_json}. Create seo_title (50-60 chars), meta_description (120-155 chars), h1 (string), outline (array of {heading, notes, target_word_count}), total_target_word_count (number), tone_and_voice (string), primary_keyword (string), secondary_keywords (array), internal_link_suggestions (array), external_source_suggestions (array), primary_cta (string), media_recommendations (array).",
        [
          { field: "seo_title", as: "brief_seo_title", type: "string" },
          {
            field: "meta_description",
            as: "brief_meta_description",
            type: "string",
          },
          { field: "outline", as: "brief_outline_json", type: "string" },
          {
            field: "total_target_word_count",
            as: "brief_target_words",
            type: "number",
          },
          { field: "tone_and_voice", as: "brief_tone", type: "string" },
          { field: "primary_cta", as: "brief_primary_cta", type: "string" },
          {
            field: "secondary_keywords",
            as: "brief_secondary_keywords_json",
            type: "string",
          },
        ],
      ),

      // 7. Brief quality validator (runCode)
      n("7", "runCode", "Validate brief quality and completeness", 1480, {
        runCodeDetails: {
          language: "javascript",
          code: `// Validate the generated brief meets quality standards
const outline = JSON.parse(vars.brief_outline_json || "[]");
const targetWords = parseInt(vars.brief_target_words) || 0;
const seoTitle = vars.brief_seo_title || "";
const metaDesc = vars.brief_meta_description || "";
const checks = [];
let score = 100;
if (seoTitle.length < 30 || seoTitle.length > 65) { checks.push("SEO title length: " + seoTitle.length + " chars"); score -= 10; }
if (metaDesc.length < 70 || metaDesc.length > 165) { checks.push("Meta description length: " + metaDesc.length + " chars"); score -= 10; }
if (outline.length < 3) { checks.push("Outline too short: only " + outline.length + " sections"); score -= 15; }
if (targetWords < 500) { checks.push("Target word count too low: " + targetWords); score -= 15; }
const outlineWords = outline.reduce((sum, s) => sum + (s.target_word_count || 0), 0);
if (Math.abs(outlineWords - targetWords) > targetWords * 0.3) { checks.push("Section word counts do not sum to target"); score -= 10; }
if (!vars.brief_primary_cta || vars.brief_primary_cta.length < 5) { checks.push("Missing or weak primary CTA"); score -= 10; }
return {
  quality_score: Math.max(score, 0),
  quality_checks_json: JSON.stringify(checks),
  issues_found: checks.length,
  validation_status: score >= 70 ? "pass" : "needs_revision",
  seo_title_length: seoTitle.length,
  meta_desc_length: metaDesc.length,
  outline_section_count: outline.length
};`,
          outputVariables: [
            { name: "quality_score", type: "number" },
            { name: "quality_checks_json", type: "string" },
            { name: "issues_found", type: "number" },
            { name: "validation_status", type: "string" },
            { name: "seo_title_length", type: "number" },
            { name: "meta_desc_length", type: "number" },
            { name: "outline_section_count", type: "number" },
          ],
        },
      }),

      // 8. Brief delivery (Sheets)
      sheetsNode(
        "8",
        "Log content brief to Sheets",
        1720,
        "Content_Briefs",
        `[["${new Date().toISOString()}","{brief_seo_title}","{primary_search_intent}","{expected_format}","{brief_target_words}","{outline_section_count}","{quality_score}","{validation_status}","{brief_tone}","{brief_primary_cta}"]]`,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1100. SOURCE BIBLIOGRAPHY BUILDER (7 nodes)
  // ===================================================================

  "Source Bibliography Builder": {
    summary:
      "Takes a content topic, finds academic and industry sources via SerpAPI, evaluates source credibility with AI, extracts key statistics, formats citations with code, builds a bibliography in Sheets, and delivers to the content team via Slack.",
    nodes: [
      trigger("Webhook: Content topic for source research", "webhook_app", {
        source: "internal",
        event: "bibliography_request",
        howItStarts:
          "A content topic is submitted to research and compile a curated bibliography of authoritative sources, statistics, and citations for EEAT-focused content creation",
        setup:
          "Configure SerpAPI for academic and industry source discovery. Set up OpenAI for credibility evaluation and statistic extraction. Provide Sheets for bibliography output and Slack for delivery.",
        inputFields: [],
      }),

      // 2. Academic + industry source finder (SerpAPI)
      n("2", "hitApi", "SerpAPI: Find academic and industry sources", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://serpapi.com/search.json?q={content_topic}+research+statistics+study&api_key={serpapi_key}&engine=google_scholar",
          headers: [],
          body: [],
          variables: [
            {
              name: "organic_results",
              customName: "scholar_results_json",
              type: "string",
            },
            {
              name: "search_information.total_results",
              customName: "scholar_total_results",
              type: "number",
            },
          ],
        },
      }),

      // 3. Source credibility evaluator (AI)
      aiNode(
        "3",
        "AI: Evaluate source credibility and authority",
        520,
        "You are a research librarian and fact-checker. Evaluate sources for credibility, authority, recency, and relevance. Score each source and flag potential issues like retracted studies, biased organizations, or outdated data. Return JSON.",
        "Evaluate these sources for topic from webhook: {webhook_payload}. Scholar results: {scholar_results_json}. For each source, assess credibility_score (1-10), authority_type (academic/government/industry/media/blog), publication_year, relevance_to_topic (high/medium/low), potential_bias (none/low/moderate/high), recommended_use (primary_source/supporting/background/avoid). Return evaluated_sources (array), total_evaluated (number), high_credibility_count (number), recommended_primary_sources (number), source_diversity_score (number 1-10).",
        [
          {
            field: "evaluated_sources",
            as: "evaluated_sources_json",
            type: "string",
          },
          {
            field: "total_evaluated",
            as: "total_sources_evaluated",
            type: "number",
          },
          {
            field: "high_credibility_count",
            as: "high_credibility_count",
            type: "number",
          },
          {
            field: "recommended_primary_sources",
            as: "primary_source_count",
            type: "number",
          },
          {
            field: "source_diversity_score",
            as: "source_diversity_score",
            type: "number",
          },
        ],
      ),

      // 4. Key statistic extractor (AI)
      aiNode(
        "4",
        "AI: Extract key statistics from sources",
        760,
        "You are a data analyst who extracts the most compelling and citation-worthy statistics from research sources. Focus on unique, recent, and impactful data points that strengthen content authority. Return JSON.",
        "Extract key statistics from these evaluated sources: {evaluated_sources_json}. Topic: {webhook_payload}. For each high-credibility source, extract statistic (the data point), context (brief explanation), source_attribution (proper citation), year (publication year), impact_score (1-10 for how compelling it is). Return statistics (array of {statistic, context, source_attribution, year, impact_score}), total_statistics (number), most_compelling_stat (string), data_recency_avg_years (number).",
        [
          {
            field: "statistics",
            as: "extracted_statistics_json",
            type: "string",
          },
          {
            field: "total_statistics",
            as: "total_statistics_extracted",
            type: "number",
          },
          {
            field: "most_compelling_stat",
            as: "most_compelling_stat",
            type: "string",
          },
          {
            field: "data_recency_avg_years",
            as: "avg_data_recency",
            type: "number",
          },
        ],
      ),

      // 5. Citation formatter (runCode)
      n("5", "runCode", "Format citations in APA and inline styles", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Format sources into proper APA citations and inline references
const sources = JSON.parse(vars.evaluated_sources_json || "[]");
const stats = JSON.parse(vars.extracted_statistics_json || "[]");
const citations = sources.filter(s => s.recommended_use !== "avoid").map((s, i) => {
  const result = s.result || s;
  const title = result.title || "Untitled";
  const link = result.link || result.url || "";
  const snippet = result.snippet || "";
  const year = s.publication_year || "n.d.";
  const authors = result.authors || "Unknown";
  const apa = authors + " (" + year + "). " + title + ". Retrieved from " + link;
  const inline = "(" + authors.split(",")[0].trim() + ", " + year + ")";
  return { id: i + 1, title, link, apa_citation: apa, inline_citation: inline, credibility: s.credibility_score, use: s.recommended_use };
});
const statCitations = stats.map(s => ({
  statistic: s.statistic,
  citation: s.source_attribution,
  formatted: '"' + s.statistic + '" ' + s.source_attribution
}));
return {
  formatted_citations_json: JSON.stringify(citations),
  stat_citations_json: JSON.stringify(statCitations),
  total_formatted: citations.length,
  total_stat_citations: statCitations.length,
  bibliography_status: citations.length > 0 ? "ready" : "empty"
};`,
          outputVariables: [
            { name: "formatted_citations_json", type: "string" },
            { name: "stat_citations_json", type: "string" },
            { name: "total_formatted", type: "number" },
            { name: "total_stat_citations", type: "number" },
            { name: "bibliography_status", type: "string" },
          ],
        },
      }),

      // 6. Bibliography builder (Sheets)
      sheetsNode(
        "6",
        "Log bibliography to Sheets",
        1240,
        "Bibliographies",
        `[["${new Date().toISOString()}","{content_topic}","{total_sources_evaluated}","{high_credibility_count}","{primary_source_count}","{source_diversity_score}","{total_statistics_extracted}","{most_compelling_stat}","{total_formatted}","{bibliography_status}"]]`,
      ),

      // 7. Content team delivery (Slack)
      slackNode(
        "7",
        "Slack: Deliver bibliography to content team",
        1480,
        "Source Bibliography Ready\\n==========================\\nTopic: {content_topic}\\nSources Evaluated: {total_sources_evaluated}\\nHigh Credibility: {high_credibility_count}\\nPrimary Sources: {primary_source_count}\\nDiversity Score: {source_diversity_score}/10\\n\\nStatistics Extracted: {total_statistics_extracted}\\nAvg Data Recency: {avg_data_recency} years\\nMost Compelling: {most_compelling_stat}\\n\\nFormatted Citations: {total_formatted}\\nStat Citations: {total_stat_citations}\\n\\nFull bibliography with APA citations and inline references in Sheets.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1101. FAQ SCHEMA DRAFTER (6 nodes)
  // ===================================================================

  "FAQ Schema Drafter": {
    summary:
      "Takes a page URL, extracts page content with code, enhances questions with AI, generates JSON-LD FAQ schema with code, validates the schema, and delivers via Slack.",
    nodes: [
      trigger("Webhook: Page URL for FAQ schema generation", "webhook_app", {
        source: "internal",
        event: "faq_schema_request",
        howItStarts:
          "A page URL is submitted to extract existing FAQ content and generate optimized FAQPage JSON-LD schema markup ready for implementation",
        setup:
          "Provide the target page URL. Configure OpenAI for question enhancement. Set up Slack for schema delivery to the dev team.",
        inputFields: [],
      }),

      // 2. Page content extractor (runCode)
      n("2", "runCode", "Extract FAQ content from page", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Extract FAQ-like content from the page payload
const payload = JSON.parse(vars.webhook_payload || "{}");
const pageUrl = payload.url || payload.page_url || "";
const pageContent = payload.page_content || payload.html || "";
const headings = payload.headings || [];
const paragraphs = payload.paragraphs || [];
// Look for question-like patterns in headings and content
const questionPatterns = /^(what|how|why|when|where|who|which|can|do|does|is|are|will|should|would)\\b/i;
const questions = [];
headings.forEach((h, i) => {
  if (questionPatterns.test(h.text || h)) {
    const text = h.text || h;
    const answer = paragraphs[i] || paragraphs.find(p => (typeof p === "string" ? p : p.text || "").length > 20) || "";
    questions.push({ question: text, answer: typeof answer === "string" ? answer : answer.text || "", source: "heading" });
  }
});
// Also check for dl/dt/dd or FAQ section patterns
const faqSection = (pageContent.match(/<(dt|h[2-4])[^>]*>(.*?)<\\/\\1>\\s*<(dd|p)[^>]*>(.*?)<\\/\\3>/gi) || []);
faqSection.forEach(match => {
  const q = match.replace(/<[^>]+>/g, " ").trim().split(/\\s{2,}/);
  if (q.length >= 2) questions.push({ question: q[0], answer: q.slice(1).join(" "), source: "html_structure" });
});
return {
  extracted_faqs_json: JSON.stringify(questions),
  total_extracted: questions.length,
  page_url: pageUrl,
  extraction_method: questions.length > 0 ? "pattern_match" : "none_found",
  needs_enhancement: questions.length < 3 ? "true" : "false"
};`,
          outputVariables: [
            { name: "extracted_faqs_json", type: "string" },
            { name: "total_extracted", type: "number" },
            { name: "page_url", type: "string" },
            { name: "extraction_method", type: "string" },
            { name: "needs_enhancement", type: "string" },
          ],
        },
      }),

      // 3. AI question enhancer
      aiNode(
        "3",
        "AI: Enhance and expand FAQ questions",
        520,
        "You are an FAQ optimization specialist. Improve existing FAQ questions for clarity and snippet-friendliness, and suggest additional questions users commonly ask about the topic. Write concise, authoritative answers. Return JSON.",
        "Enhance these FAQs from page {page_url}: {extracted_faqs_json}. Total extracted: {total_extracted}. Needs enhancement: {needs_enhancement}. For each existing FAQ, rewrite enhanced_question (clearer, snippet-optimized), enhanced_answer (concise 2-3 sentences). Also add 3-5 new suggested questions with answers based on the topic. Return enhanced_faqs (array of {question, answer, is_new}), total_enhanced (number), total_new_suggestions (number).",
        [
          { field: "enhanced_faqs", as: "enhanced_faqs_json", type: "string" },
          {
            field: "total_enhanced",
            as: "total_enhanced_faqs",
            type: "number",
          },
          {
            field: "total_new_suggestions",
            as: "total_new_suggestions",
            type: "number",
          },
        ],
      ),

      // 4. JSON-LD schema generator (runCode)
      n("4", "runCode", "Generate FAQPage JSON-LD schema", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Generate valid FAQPage JSON-LD from enhanced FAQs
const faqs = JSON.parse(vars.enhanced_faqs_json || "[]");
const pageUrl = vars.page_url || "";
const schema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faqs.map(f => ({
    "@type": "Question",
    "name": f.question,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": f.answer
    }
  }))
};
const schemaString = JSON.stringify(schema, null, 2);
const scriptTag = '<script type="application/ld+json">' + schemaString + '</script>';
return {
  faq_schema_json: schemaString,
  faq_script_tag: scriptTag,
  schema_question_count: faqs.length,
  schema_byte_size: schemaString.length,
  implementation_ready: faqs.length > 0 ? "true" : "false"
};`,
          outputVariables: [
            { name: "faq_schema_json", type: "string" },
            { name: "faq_script_tag", type: "string" },
            { name: "schema_question_count", type: "number" },
            { name: "schema_byte_size", type: "number" },
            { name: "implementation_ready", type: "string" },
          ],
        },
      }),

      // 5. Schema validator (condition)
      n(
        "5",
        "condition",
        "Is FAQ schema valid and ready for implementation?",
        1000,
        {
          conditionData: [
            {
              variable: "schema_question_count",
              value: "0",
              comparison_operator: "GREATER THAN",
              logical_operator: "AND",
            },
            {
              variable: "implementation_ready",
              value: "true",
              comparison_operator: "EQUALS",
              logical_operator: "AND",
            },
          ],
        },
      ),

      // 6. Schema delivery (Slack)
      slackNode(
        "6",
        "Slack: Deliver FAQ schema to dev team",
        1240,
        "FAQ Schema Ready for Implementation\\n=====================================\\nPage: {page_url}\\nQuestions in Schema: {schema_question_count}\\nOriginal FAQs: {total_extracted}\\nEnhanced: {total_enhanced_faqs}\\nNew Suggestions: {total_new_suggestions}\\nSchema Size: {schema_byte_size} bytes\\n\\nCopy the JSON-LD script tag below and add it to the page <head>:\\n\\n{faq_script_tag}\\n\\nValidate at: https://search.google.com/test/rich-results",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // valid schema -> deliver
      e("5", "6", "output_2"), // invalid -> still notify with issues
    ],
  },

  // ===================================================================
  // 1102. HOW-TO SCHEMA DRAFTER (7 nodes)
  // ===================================================================

  "How-To Schema Drafter": {
    summary:
      "Takes a tutorial page URL, extracts steps with code, optimizes steps with AI, generates HowTo JSON-LD with code, validates the schema, creates an implementation preview, and hands off to the dev team via Slack.",
    nodes: [
      trigger("Webhook: Tutorial page for HowTo schema", "webhook_app", {
        source: "internal",
        event: "howto_schema_request",
        howItStarts:
          "A tutorial or how-to page URL is submitted to extract steps and generate optimized HowTo JSON-LD schema markup for rich result eligibility",
        setup:
          "Provide the target tutorial page URL with step content. Configure OpenAI for step optimization. Set up Slack for dev team handoff.",
        inputFields: [],
      }),

      // 2. Step extractor (runCode)
      n("2", "runCode", "Extract tutorial steps from page content", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Extract numbered steps from tutorial page content
const payload = JSON.parse(vars.webhook_payload || "{}");
const pageUrl = payload.url || payload.page_url || "";
const pageTitle = payload.title || "";
const headings = payload.headings || [];
const paragraphs = payload.paragraphs || [];
const orderedLists = payload.ordered_lists || [];
const steps = [];
// Method 1: Extract from ordered lists
orderedLists.forEach(list => {
  (list.items || list || []).forEach((item, i) => {
    const text = typeof item === "string" ? item : item.text || "";
    if (text.length > 10) steps.push({ step_number: i + 1, text: text.trim(), source: "ordered_list" });
  });
});
// Method 2: Extract from headings with step/number patterns
if (steps.length === 0) {
  const stepPattern = /^(step\\s*\\d+|\\d+[\\.\\)\\:])/i;
  headings.forEach((h, i) => {
    const text = typeof h === "string" ? h : h.text || "";
    if (stepPattern.test(text)) {
      const desc = paragraphs[i] ? (typeof paragraphs[i] === "string" ? paragraphs[i] : paragraphs[i].text || "") : "";
      steps.push({ step_number: steps.length + 1, text: text.replace(stepPattern, "").trim(), description: desc, source: "heading" });
    }
  });
}
return {
  extracted_steps_json: JSON.stringify(steps),
  total_steps: steps.length,
  page_url: pageUrl,
  page_title: pageTitle,
  extraction_source: steps.length > 0 ? steps[0].source : "none"
};`,
          outputVariables: [
            { name: "extracted_steps_json", type: "string" },
            { name: "total_steps", type: "number" },
            { name: "page_url", type: "string" },
            { name: "page_title", type: "string" },
            { name: "extraction_source", type: "string" },
          ],
        },
      }),

      // 3. AI step optimizer
      aiNode(
        "3",
        "AI: Optimize steps for HowTo schema",
        520,
        "You are a technical writing expert specializing in structured how-to content. Optimize each step for clarity, actionability, and HowTo schema compliance. Each step needs a clear name and detailed text. Return JSON.",
        "Optimize these tutorial steps from {page_url} ({page_title}): {extracted_steps_json}. Total steps: {total_steps}. For each step, create optimized_name (short action phrase), optimized_text (detailed instruction, 1-2 sentences), estimated_time_minutes (number), tools_needed (array, if any), tips (optional helpful note). Also suggest total_time_estimate (string), difficulty_level (beginner/intermediate/advanced), supply_list (array of materials needed). Return optimized_steps (array), total_time_estimate, difficulty_level, supply_list.",
        [
          {
            field: "optimized_steps",
            as: "optimized_steps_json",
            type: "string",
          },
          {
            field: "total_time_estimate",
            as: "total_time_estimate",
            type: "string",
          },
          { field: "difficulty_level", as: "difficulty_level", type: "string" },
          { field: "supply_list", as: "supply_list_json", type: "string" },
        ],
      ),

      // 4. HowTo JSON-LD generator (runCode)
      n("4", "runCode", "Generate HowTo JSON-LD schema", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Generate valid HowTo JSON-LD from optimized steps
const steps = JSON.parse(vars.optimized_steps_json || "[]");
const supplies = JSON.parse(vars.supply_list_json || "[]");
const pageTitle = vars.page_title || "";
const pageUrl = vars.page_url || "";
const totalTime = vars.total_time_estimate || "PT30M";
const schema = {
  "@context": "https://schema.org",
  "@type": "HowTo",
  "name": pageTitle,
  "description": "Step-by-step guide: " + pageTitle,
  "totalTime": totalTime.startsWith("PT") ? totalTime : "PT" + totalTime.replace(/[^0-9HM]/g, ""),
  "estimatedCost": { "@type": "MonetaryAmount", "currency": "USD", "value": "0" },
  "supply": supplies.map(s => ({ "@type": "HowToSupply", "name": typeof s === "string" ? s : s.name || s })),
  "step": steps.map((s, i) => ({
    "@type": "HowToStep",
    "position": i + 1,
    "name": s.optimized_name || s.name || "Step " + (i + 1),
    "text": s.optimized_text || s.text || "",
    "url": pageUrl + "#step-" + (i + 1)
  }))
};
const schemaString = JSON.stringify(schema, null, 2);
const scriptTag = '<script type="application/ld+json">' + schemaString + '</script>';
return {
  howto_schema_json: schemaString,
  howto_script_tag: scriptTag,
  schema_step_count: steps.length,
  schema_byte_size: schemaString.length,
  has_supplies: supplies.length > 0 ? "true" : "false",
  implementation_ready: steps.length >= 2 ? "true" : "false"
};`,
          outputVariables: [
            { name: "howto_schema_json", type: "string" },
            { name: "howto_script_tag", type: "string" },
            { name: "schema_step_count", type: "number" },
            { name: "schema_byte_size", type: "number" },
            { name: "has_supplies", type: "string" },
            { name: "implementation_ready", type: "string" },
          ],
        },
      }),

      // 5. Schema validator (condition)
      n("5", "condition", "Is HowTo schema valid?", 1000, {
        conditionData: [
          {
            variable: "schema_step_count",
            value: "1",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
          {
            variable: "implementation_ready",
            value: "true",
            comparison_operator: "EQUALS",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Implementation preview (AI)
      aiNode(
        "6",
        "AI: Generate implementation preview and instructions",
        1240,
        "You are a technical SEO implementation specialist. Create clear implementation instructions for adding HowTo schema to a page, including where to place it, testing steps, and expected rich result appearance. Return JSON.",
        "Create implementation instructions for HowTo schema on {page_url}. Schema has {schema_step_count} steps, {schema_byte_size} bytes. Has supplies: {has_supplies}. Difficulty: {difficulty_level}. Total time: {total_time_estimate}. Provide placement_instructions (string), testing_checklist (array of steps), expected_rich_result_description (string), common_errors_to_avoid (array), estimated_implementation_time (string).",
        [
          {
            field: "placement_instructions",
            as: "placement_instructions",
            type: "string",
          },
          {
            field: "testing_checklist",
            as: "testing_checklist_json",
            type: "string",
          },
          {
            field: "expected_rich_result_description",
            as: "rich_result_preview",
            type: "string",
          },
          {
            field: "estimated_implementation_time",
            as: "impl_time_estimate",
            type: "string",
          },
        ],
      ),

      // 7. Dev handoff (Slack)
      slackNode(
        "7",
        "Slack: Hand off HowTo schema to dev team",
        1480,
        "HowTo Schema Ready\\n====================\\nPage: {page_url}\\nTitle: {page_title}\\nSteps: {schema_step_count}\\nDifficulty: {difficulty_level}\\nTotal Time: {total_time_estimate}\\nSchema Size: {schema_byte_size} bytes\\n\\nPlacement: {placement_instructions}\\nImpl Time: {impl_time_estimate}\\n\\nExpected Rich Result: {rich_result_preview}\\n\\n{howto_script_tag}\\n\\nValidate at: https://search.google.com/test/rich-results",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // valid -> implementation preview
      e("5", "7", "output_2"), // invalid -> notify with issues
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1103. AUTHOR BIO / EEAT BUILDER (8 nodes)
  // ===================================================================

  "Author Bio EEAT Builder": {
    summary:
      "Takes author info, researches their authority via SerpAPI, generates an AI bio, creates Person schema with code, builds an author page template, sets up cross-content attribution, logs to Sheets, and hands off implementation via Slack.",
    nodes: [
      trigger("Webhook: Author info for EEAT profile", "webhook_app", {
        source: "internal",
        event: "author_eeat_request",
        howItStarts:
          "Author details (name, role, expertise areas) are submitted to build a comprehensive EEAT-optimized author profile with schema markup and cross-content attribution",
        setup:
          "Configure SerpAPI for author authority research. Set up OpenAI for bio generation and template building. Provide Sheets for author profile tracking and Slack for implementation handoff.",
        inputFields: [],
      }),

      // 2. Author authority researcher (SerpAPI)
      n("2", "hitApi", "SerpAPI: Research author authority signals", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://serpapi.com/search.json?q={author_name}+{expertise_area}&api_key={serpapi_key}&engine=google&gl=us&num=20",
          headers: [],
          body: [],
          variables: [
            {
              name: "organic_results",
              customName: "author_serp_results",
              type: "string",
            },
            {
              name: "knowledge_graph",
              customName: "author_knowledge_graph",
              type: "string",
            },
            {
              name: "search_information.total_results",
              customName: "author_total_mentions",
              type: "number",
            },
          ],
        },
      }),

      // 3. AI bio generator
      aiNode(
        "3",
        "AI: Generate EEAT-optimized author bio",
        520,
        "You are an EEAT and author authority specialist. Generate author bios that demonstrate Experience, Expertise, Authoritativeness, and Trustworthiness. Include credentials, experience, publications, and social proof. Return JSON.",
        "Generate an EEAT-optimized author bio. Author info from webhook: {webhook_payload}. Online presence: {author_serp_results}. Knowledge graph: {author_knowledge_graph}. Total online mentions: {author_total_mentions}. Create short_bio (2-3 sentences for bylines), full_bio (3-4 paragraphs for author page), credentials_summary (bulleted list of qualifications), expertise_areas (array), years_experience (number), authority_signals (array of {signal, evidence}), trust_indicators (array), social_profiles (array of {platform, suggested_url}). Return bio_data (object with all fields), eeat_score (number 1-100), improvement_suggestions (array).",
        [
          { field: "bio_data", as: "bio_data_json", type: "string" },
          { field: "eeat_score", as: "eeat_score", type: "number" },
          {
            field: "improvement_suggestions",
            as: "eeat_improvements_json",
            type: "string",
          },
        ],
      ),

      // 4. Person schema generator (runCode)
      n("4", "runCode", "Generate Person JSON-LD schema for author", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Generate Person schema for EEAT author profile
const bioData = JSON.parse(vars.bio_data_json || "{}");
const payload = JSON.parse(vars.webhook_payload || "{}");
const authorName = payload.author_name || bioData.name || "";
const schema = {
  "@context": "https://schema.org",
  "@type": "Person",
  "name": authorName,
  "jobTitle": payload.job_title || bioData.job_title || "",
  "description": bioData.short_bio || "",
  "knowsAbout": bioData.expertise_areas || [],
  "alumniOf": payload.education ? [{
    "@type": "EducationalOrganization",
    "name": payload.education
  }] : [],
  "worksFor": payload.company ? {
    "@type": "Organization",
    "name": payload.company
  } : undefined,
  "sameAs": (bioData.social_profiles || []).map(s => s.suggested_url).filter(Boolean),
  "url": payload.author_page_url || ""
};
// Clean undefined values
Object.keys(schema).forEach(k => { if (schema[k] === undefined) delete schema[k]; });
const schemaString = JSON.stringify(schema, null, 2);
return {
  person_schema_json: schemaString,
  person_script_tag: '<script type="application/ld+json">' + schemaString + '</script>',
  schema_fields_populated: Object.keys(schema).filter(k => k !== "@context" && k !== "@type").length,
  author_name: authorName,
  schema_status: authorName ? "generated" : "needs_name"
};`,
          outputVariables: [
            { name: "person_schema_json", type: "string" },
            { name: "person_script_tag", type: "string" },
            { name: "schema_fields_populated", type: "number" },
            { name: "author_name", type: "string" },
            { name: "schema_status", type: "string" },
          ],
        },
      }),

      // 5. Author page template builder (AI)
      aiNode(
        "5",
        "AI: Build author page template",
        1000,
        "You are a web content architect. Design an author page template that maximizes EEAT signals for Google while providing genuine value to readers. Include all sections that demonstrate expertise and trust. Return JSON.",
        "Build an author page template for {author_name}. Bio data: {bio_data_json}. EEAT score: {eeat_score}. Schema fields: {schema_fields_populated}. Design page_sections (array of {section_name, content_guidance, eeat_signal}), page_title (string), meta_description (string), h1 (string), recommended_sidebar_elements (array), internal_linking_strategy (string), social_proof_placement (array). Return author_page_template (object with all fields).",
        [
          {
            field: "author_page_template",
            as: "author_page_template_json",
            type: "string",
          },
        ],
      ),

      // 6. Cross-content author attribution (AI)
      aiNode(
        "6",
        "AI: Plan cross-content author attribution",
        1240,
        "You are an EEAT implementation strategist. Plan how to attribute the author across all their content with consistent bylines, author boxes, and schema linking. Return JSON.",
        "Plan cross-content attribution for {author_name}. EEAT score: {eeat_score}. Bio data: {bio_data_json}. Person schema: {person_schema_json}. Design byline_format (string), author_box_template (HTML structure description), article_schema_author_snippet (JSON-LD snippet to add to each article), attribution_checklist (array of implementation steps), content_types_to_attribute (array of {content_type, attribution_method}), estimated_implementation_scope (string). Return attribution_plan (object with all fields).",
        [
          {
            field: "attribution_plan",
            as: "attribution_plan_json",
            type: "string",
          },
        ],
      ),

      // 7. Author page report (Sheets)
      sheetsNode(
        "7",
        "Log author EEAT profile to Sheets",
        1480,
        "Author_EEAT",
        `[["${new Date().toISOString()}","{author_name}","{eeat_score}","{schema_fields_populated}","{author_total_mentions}","{schema_status}"]]`,
      ),

      // 8. Implementation handoff (Slack)
      slackNode(
        "8",
        "Slack: Hand off author EEAT implementation",
        1720,
        "Author EEAT Profile Ready\\n===========================\\nAuthor: {author_name}\\nEEAT Score: {eeat_score}/100\\nOnline Mentions: {author_total_mentions}\\nSchema Fields Populated: {schema_fields_populated}\\n\\nDeliverables:\\n1. Person JSON-LD schema (ready to implement)\\n2. Author page template (sections and content guidance)\\n3. Cross-content attribution plan (bylines, author boxes, article schema)\\n\\nPerson Schema:\\n{person_script_tag}\\n\\nFull profile, template, and attribution plan in Sheets.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1104. ARTICLE UPDATE SCORER (7 nodes)
  // ===================================================================

  "Article Update Scorer": {
    summary:
      "Scans the content inventory, fetches traffic trends from GA4, tracks ranking positions via SerpAPI, calculates freshness decay with code, uses AI to score update priority, logs the priority report to Sheets, and distributes assignments via Slack.",
    nodes: [
      trigger("Schedule: Content inventory update scoring", "schedule", {
        schedule: "0 6 * * 4 (every Thursday at 6 AM)",
        howItStarts:
          "A scheduled job scans the content inventory and scores each article for update urgency based on traffic trends, ranking changes, and content freshness decay",
        setup:
          "Connect GA4 for traffic data. Configure SerpAPI for ranking tracking. Provide content inventory with publication dates. Set up Sheets for the priority report and Slack for team distribution.",
        inputFields: [],
      }),

      // 2. Traffic trend fetch (GA4)
      ga4Node(
        "2",
        "GA4: Fetch article traffic trends",
        280,
        '[{"name":"pagePath"},{"name":"date"}]',
        '[{"name":"sessions"},{"name":"screenPageViews"},{"name":"averageSessionDuration"}]',
        [
          { name: "rows", customName: "article_traffic_rows", type: "string" },
          { name: "rowCount", customName: "article_count", type: "number" },
        ],
      ),

      // 3. Ranking position tracker (SerpAPI)
      n("3", "hitApi", "SerpAPI: Track article keyword positions", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://serpapi.com/search.json?q=site:{your_domain}&api_key={serpapi_key}&num=100&gl=us",
          headers: [],
          body: [],
          variables: [
            {
              name: "organic_results",
              customName: "article_ranking_data",
              type: "string",
            },
            {
              name: "search_information.total_results",
              customName: "total_indexed_articles",
              type: "number",
            },
          ],
        },
      }),

      // 4. Freshness decay calculator (runCode)
      n("4", "runCode", "Calculate freshness decay scores", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Calculate freshness decay combining traffic trends, ranking, and age
const trafficRows = JSON.parse(vars.article_traffic_rows || "[]");
const rankings = JSON.parse(vars.article_ranking_data || "[]");
const now = Date.now();
const dayMs = 86400000;
const articles = {};
// Aggregate traffic by page
trafficRows.forEach(row => {
  const dims = row.dimensionValues || [];
  const mets = row.metricValues || [];
  const path = dims[0] ? dims[0].value : "";
  const date = dims[1] ? dims[1].value : "";
  const sessions = mets[0] ? parseInt(mets[0].value) : 0;
  if (!articles[path]) articles[path] = { path, sessions_recent: 0, sessions_old: 0, dates: [] };
  const dateNum = new Date(date.replace(/(\\d{4})(\\d{2})(\\d{2})/, "$1-$2-$3")).getTime();
  if (now - dateNum < 30 * dayMs) articles[path].sessions_recent += sessions;
  else articles[path].sessions_old += sessions;
  articles[path].dates.push(dateNum);
});
const scored = Object.values(articles).map(a => {
  const trendDecline = a.sessions_old > 0 ? Math.round(((a.sessions_old - a.sessions_recent) / a.sessions_old) * 100) : 0;
  const rankData = rankings.find(r => (r.link || "").includes(a.path));
  const position = rankData ? rankData.position || 50 : 50;
  const positionPenalty = position > 20 ? 30 : position > 10 ? 15 : position > 5 ? 5 : 0;
  const declineScore = Math.max(trendDecline, 0);
  const updateScore = Math.min(Math.round(declineScore * 0.5 + positionPenalty + (a.sessions_recent < 5 ? 20 : 0)), 100);
  return { path: a.path, update_score: updateScore, trend_decline_pct: trendDecline, position, sessions_recent: a.sessions_recent, sessions_old: a.sessions_old, urgency: updateScore > 70 ? "urgent" : updateScore > 40 ? "moderate" : "low" };
});
scored.sort((a, b) => b.update_score - a.update_score);
return {
  scored_articles_json: JSON.stringify(scored.slice(0, 40)),
  total_articles_scored: scored.length,
  urgent_count: scored.filter(s => s.urgency === "urgent").length,
  moderate_count: scored.filter(s => s.urgency === "moderate").length,
  top_priority_path: scored.length > 0 ? scored[0].path : "none",
  top_priority_score: scored.length > 0 ? scored[0].update_score : 0
};`,
          outputVariables: [
            { name: "scored_articles_json", type: "string" },
            { name: "total_articles_scored", type: "number" },
            { name: "urgent_count", type: "number" },
            { name: "moderate_count", type: "number" },
            { name: "top_priority_path", type: "string" },
            { name: "top_priority_score", type: "number" },
          ],
        },
      }),

      // 5. AI update priority scorer
      aiNode(
        "5",
        "AI: Score and prioritize article updates",
        1000,
        "You are a content operations strategist. Analyze article update scores and recommend specific update actions ranked by expected traffic recovery and effort required. Return JSON.",
        "Prioritize article updates from these scores: {scored_articles_json}. Total scored: {total_articles_scored}. Urgent: {urgent_count}. Moderate: {moderate_count}. Top priority: {top_priority_path} (score: {top_priority_score}). For each urgent and moderate article, recommend update_action (full_rewrite/major_update/minor_refresh/data_update), estimated_hours, expected_traffic_recovery_pct, specific_recommendations (array of changes). Return prioritized_updates (array), total_update_hours (number), expected_total_recovery (string), sprint_plan (array of {week, articles, total_hours}).",
        [
          {
            field: "prioritized_updates",
            as: "prioritized_updates_json",
            type: "string",
          },
          {
            field: "total_update_hours",
            as: "total_update_hours",
            type: "number",
          },
          {
            field: "expected_total_recovery",
            as: "expected_recovery",
            type: "string",
          },
          { field: "sprint_plan", as: "sprint_plan_json", type: "string" },
        ],
      ),

      // 6. Update priority report (Sheets)
      sheetsNode(
        "6",
        "Log update priority report to Sheets",
        1240,
        "Article_Updates",
        `[["${new Date().toISOString()}","{total_articles_scored}","{urgent_count}","{moderate_count}","{top_priority_path}","{top_priority_score}","{total_update_hours}","{expected_recovery}"]]`,
      ),

      // 7. Team distribution (Slack)
      slackNode(
        "7",
        "Slack: Distribute update assignments to team",
        1480,
        "Article Update Priority Report\\n================================\\nArticles Scored: {total_articles_scored}\\nUrgent Updates: {urgent_count}\\nModerate Updates: {moderate_count}\\n\\nTop Priority: {top_priority_path} (score: {top_priority_score}/100)\\nTotal Hours Needed: {total_update_hours}\\nExpected Recovery: {expected_recovery}\\n\\nSprint plan and detailed update recommendations in Sheets.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1105. SEO WEEKLY DIGEST (9 nodes)
  // ===================================================================

  "SEO Weekly Digest": {
    summary:
      "Runs on a weekly schedule, fetches organic traffic from GA4, tracks ranking changes via SerpAPI, aggregates content performance, monitors technical SEO, analyzes everything with AI, builds a digest report in Sheets, distributes to the team via Slack, and escalates alerts based on a severity condition.",
    nodes: [
      trigger("Schedule: Weekly SEO digest", "schedule", {
        schedule: "0 8 * * 1 (every Monday at 8 AM)",
        howItStarts:
          "A weekly scheduled job compiles a comprehensive SEO performance digest covering organic traffic, rankings, content performance, and technical health",
        setup:
          "Connect GA4 for organic traffic data. Configure SerpAPI for ranking monitoring. Set up OpenAI for analysis. Provide Sheets for the digest report and Slack for team distribution.",
        inputFields: [],
      }),

      // 2. Organic traffic fetch (GA4)
      ga4Node(
        "2",
        "GA4: Fetch weekly organic traffic data",
        280,
        '[{"name":"sessionDefaultChannelGroup"},{"name":"date"}]',
        '[{"name":"sessions"},{"name":"conversions"},{"name":"bounceRate"},{"name":"averageSessionDuration"}]',
        [
          { name: "rows", customName: "weekly_traffic_rows", type: "string" },
          {
            name: "rowCount",
            customName: "weekly_channel_count",
            type: "number",
          },
        ],
      ),

      // 3. Ranking change tracker (SerpAPI)
      n("3", "hitApi", "SerpAPI: Track weekly ranking changes", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://serpapi.com/search.json?q=site:{your_domain}&api_key={serpapi_key}&num=100&gl=us",
          headers: [],
          body: [],
          variables: [
            {
              name: "organic_results",
              customName: "weekly_ranking_results",
              type: "string",
            },
            {
              name: "search_information.total_results",
              customName: "weekly_indexed_count",
              type: "number",
            },
          ],
        },
      }),

      // 4. Content performance aggregator (runCode)
      n("4", "runCode", "Aggregate content performance metrics", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Aggregate organic performance from GA4 data
const rows = JSON.parse(vars.weekly_traffic_rows || "[]");
const channels = {};
let totalSessions = 0;
let totalConversions = 0;
rows.forEach(row => {
  const dims = row.dimensionValues || [];
  const mets = row.metricValues || [];
  const channel = dims[0] ? dims[0].value : "unknown";
  const sessions = mets[0] ? parseInt(mets[0].value) : 0;
  const conversions = mets[1] ? parseInt(mets[1].value) : 0;
  if (!channels[channel]) channels[channel] = { sessions: 0, conversions: 0 };
  channels[channel].sessions += sessions;
  channels[channel].conversions += conversions;
  totalSessions += sessions;
  totalConversions += conversions;
});
const organic = channels["Organic Search"] || { sessions: 0, conversions: 0 };
const organicShare = totalSessions > 0 ? Math.round((organic.sessions / totalSessions) * 100) : 0;
const rankings = JSON.parse(vars.weekly_ranking_results || "[]");
const topPages = rankings.slice(0, 10).map(r => ({ url: r.link, position: r.position, title: r.title }));
return {
  organic_sessions: organic.sessions,
  organic_conversions: organic.conversions,
  organic_share_pct: organicShare,
  total_sessions: totalSessions,
  total_conversions: totalConversions,
  channel_breakdown_json: JSON.stringify(channels),
  top_ranking_pages_json: JSON.stringify(topPages),
  indexed_page_count: parseInt(vars.weekly_indexed_count) || 0
};`,
          outputVariables: [
            { name: "organic_sessions", type: "number" },
            { name: "organic_conversions", type: "number" },
            { name: "organic_share_pct", type: "number" },
            { name: "total_sessions", type: "number" },
            { name: "total_conversions", type: "number" },
            { name: "channel_breakdown_json", type: "string" },
            { name: "top_ranking_pages_json", type: "string" },
            { name: "indexed_page_count", type: "number" },
          ],
        },
      }),

      // 5. Technical SEO monitor (runCode)
      n("5", "runCode", "Monitor technical SEO health indicators", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Compile technical SEO health metrics
const indexedCount = parseInt(vars.indexed_page_count) || 0;
const organicSessions = parseInt(vars.organic_sessions) || 0;
const organicShare = parseInt(vars.organic_share_pct) || 0;
const rankings = JSON.parse(vars.weekly_ranking_results || "[]");
const top3 = rankings.filter(r => r.position <= 3).length;
const top10 = rankings.filter(r => r.position <= 10).length;
const top20 = rankings.filter(r => r.position <= 20).length;
const healthIndicators = {
  indexed_pages: indexedCount,
  top_3_positions: top3,
  top_10_positions: top10,
  top_20_positions: top20,
  organic_traffic_share: organicShare,
  visibility_score: Math.min(Math.round((top10 / Math.max(rankings.length, 1)) * 100), 100)
};
const alerts = [];
if (organicShare < 30) alerts.push("Organic traffic share below 30%: " + organicShare + "%");
if (top10 < 5) alerts.push("Fewer than 5 pages in top 10 positions");
if (indexedCount < 10) alerts.push("Low indexed page count: " + indexedCount);
return {
  tech_health_json: JSON.stringify(healthIndicators),
  visibility_score: healthIndicators.visibility_score,
  top_3_count: top3,
  top_10_count: top10,
  alerts_json: JSON.stringify(alerts),
  alert_count: alerts.length,
  overall_health: alerts.length === 0 ? "healthy" : alerts.length <= 2 ? "warning" : "critical"
};`,
          outputVariables: [
            { name: "tech_health_json", type: "string" },
            { name: "visibility_score", type: "number" },
            { name: "top_3_count", type: "number" },
            { name: "top_10_count", type: "number" },
            { name: "alerts_json", type: "string" },
            { name: "alert_count", type: "number" },
            { name: "overall_health", type: "string" },
          ],
        },
      }),

      // 6. AI SEO analyst
      aiNode(
        "6",
        "AI: Analyze SEO performance and generate insights",
        1240,
        "You are a senior SEO analyst. Analyze weekly SEO data to identify trends, explain changes, highlight wins and risks, and recommend immediate actions. Provide executive-level insights with tactical specifics. Return JSON.",
        "Analyze weekly SEO performance. Organic sessions: {organic_sessions}. Organic conversions: {organic_conversions}. Organic share: {organic_share_pct}%. Visibility score: {visibility_score}. Top 3 positions: {top_3_count}. Top 10: {top_10_count}. Health: {overall_health}. Alerts: {alerts_json}. Channel breakdown: {channel_breakdown_json}. Top pages: {top_ranking_pages_json}. Indexed: {indexed_page_count}. Provide executive_summary (2-3 sentences), key_wins (array), key_risks (array), recommended_actions (array of {action, priority, expected_impact}), trend_direction (improving/stable/declining), week_over_week_assessment (string).",
        [
          {
            field: "executive_summary",
            as: "seo_executive_summary",
            type: "string",
          },
          { field: "key_wins", as: "key_wins_json", type: "string" },
          { field: "key_risks", as: "key_risks_json", type: "string" },
          {
            field: "recommended_actions",
            as: "recommended_actions_json",
            type: "string",
          },
          { field: "trend_direction", as: "trend_direction", type: "string" },
          {
            field: "week_over_week_assessment",
            as: "wow_assessment",
            type: "string",
          },
        ],
      ),

      // 7. Digest report builder (Sheets)
      sheetsNode(
        "7",
        "Log SEO weekly digest to Sheets",
        1480,
        "SEO_Digest",
        `[["${new Date().toISOString()}","{organic_sessions}","{organic_conversions}","{organic_share_pct}%","{visibility_score}","{top_3_count}","{top_10_count}","{overall_health}","{trend_direction}","{seo_executive_summary}"]]`,
      ),

      // 8. Team distribution (Slack)
      slackNode(
        "8",
        "Slack: Distribute SEO weekly digest",
        1720,
        "SEO Weekly Digest\\n==================\\n{seo_executive_summary}\\n\\nOrganic Traffic: {organic_sessions} sessions ({organic_share_pct}% of total)\\nConversions: {organic_conversions}\\nVisibility Score: {visibility_score}/100\\nTop 3 Rankings: {top_3_count} | Top 10: {top_10_count}\\nHealth: {overall_health} | Trend: {trend_direction}\\n\\nWoW: {wow_assessment}\\n\\nAlerts ({alert_count}): {alerts_json}\\n\\nFull digest with wins, risks, and action items in Sheets.",
      ),

      // 9. Alert escalation condition
      n(
        "9",
        "condition",
        "Are there critical alerts requiring escalation?",
        1960,
        {
          conditionData: [
            {
              variable: "overall_health",
              value: "critical",
              comparison_operator: "EQUALS",
              logical_operator: "OR",
            },
            {
              variable: "alert_count",
              value: "2",
              comparison_operator: "GREATER THAN",
              logical_operator: "OR",
            },
          ],
        },
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
      e("9", "8", "output_2"), // no escalation needed -> done (already sent to Slack)
    ],
  },

  // ===================================================================
  // 1106. SUPPORT TO SEO ARTICLE (8 nodes)
  // ===================================================================

  "Support to SEO Article": {
    summary:
      "Aggregates support questions, validates search demand via SerpAPI, checks for existing content coverage with code, generates an AI SEO article, plans internal links, generates schema markup with code, delivers the article draft to Sheets, and notifies the team via Slack.",
    nodes: [
      trigger("Webhook: Support question for SEO article", "webhook_app", {
        source: "internal",
        event: "support_to_seo_request",
        howItStarts:
          "A frequently asked support question is submitted to create an SEO-optimized article that reduces support ticket volume while capturing organic search traffic",
        setup:
          "Configure SerpAPI for search demand validation. Set up OpenAI for article generation. Provide your sitemap or content list for duplicate checking. Set up Sheets for draft delivery and Slack for team notification.",
        inputFields: [],
      }),

      // 2. Search demand validator (SerpAPI)
      n(
        "2",
        "hitApi",
        "SerpAPI: Validate search demand for support topic",
        280,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://serpapi.com/search.json?q={support_question}&api_key={serpapi_key}&engine=google&gl=us&num=10",
            headers: [],
            body: [],
            variables: [
              {
                name: "organic_results",
                customName: "support_serp_results",
                type: "string",
              },
              {
                name: "related_searches",
                customName: "support_related_queries",
                type: "string",
              },
              {
                name: "search_information.total_results",
                customName: "support_search_volume",
                type: "number",
              },
            ],
          },
        },
      ),

      // 3. Existing content checker (runCode)
      n("3", "runCode", "Check if existing content covers this topic", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Check if any existing content already covers this support topic
const payload = JSON.parse(vars.webhook_payload || "{}");
const question = (payload.support_question || payload.question || "").toLowerCase();
const existingContent = (payload.existing_urls || "").split(",").map(u => u.trim().toLowerCase());
const serpResults = JSON.parse(vars.support_serp_results || "[]");
const questionWords = question.split(/\\s+/).filter(w => w.length > 3);
// Check our existing content
const ourExisting = existingContent.filter(url => questionWords.some(w => url.includes(w)));
// Check if we already rank
const ourDomain = payload.domain || "";
const ourRankings = serpResults.filter(r => (r.link || "").toLowerCase().includes(ourDomain.toLowerCase()));
// Check competitor coverage
const competitorCoverage = serpResults.filter(r => !(r.link || "").toLowerCase().includes(ourDomain.toLowerCase()));
return {
  has_existing_content: ourExisting.length > 0 ? "true" : "false",
  existing_urls_json: JSON.stringify(ourExisting),
  already_ranking: ourRankings.length > 0 ? "true" : "false",
  our_best_position: ourRankings.length > 0 ? ourRankings[0].position : 0,
  competitor_count: competitorCoverage.length,
  needs_new_article: ourExisting.length === 0 && ourRankings.length === 0 ? "true" : "false",
  support_question: payload.support_question || payload.question || ""
};`,
          outputVariables: [
            { name: "has_existing_content", type: "string" },
            { name: "existing_urls_json", type: "string" },
            { name: "already_ranking", type: "string" },
            { name: "our_best_position", type: "number" },
            { name: "competitor_count", type: "number" },
            { name: "needs_new_article", type: "string" },
            { name: "support_question", type: "string" },
          ],
        },
      }),

      // 4. AI SEO article generator
      aiNode(
        "4",
        "AI: Generate SEO-optimized support article",
        760,
        "You are a content writer who creates SEO-optimized help articles that serve dual purposes: reducing support tickets and capturing organic search traffic. Write comprehensive, clear articles with proper heading structure, FAQs, and CTAs. Return JSON.",
        "Write an SEO article for support question: {support_question}. Search results context: {support_serp_results}. Related queries: {support_related_queries}. Competitor count: {competitor_count}. Needs new article: {needs_new_article}. Create seo_title (50-60 chars), meta_description (120-155 chars), h1 (string), article_body (full HTML article with H2s, H3s, paragraphs, lists), faq_section (array of {question, answer}), word_count (number), target_keyword (string), secondary_keywords (array), suggested_cta (string). Return article (object with all fields).",
        [{ field: "article", as: "seo_article_json", type: "string" }],
      ),

      // 5. Internal link planner (AI)
      aiNode(
        "5",
        "AI: Plan internal links for support article",
        1000,
        "You are an internal linking strategist. Plan contextual internal links for a new support article to connect it with existing help content, product pages, and related resources. Return JSON.",
        "Plan internal links for this support article: {seo_article_json}. Existing content: {existing_urls_json}. Support question: {support_question}. Suggest contextual_links (array of {anchor_text, target_description, placement_section}), related_articles_to_link_from (array of {source_page_description, anchor_text}), total_suggested_links (number), linking_strategy (string).",
        [
          {
            field: "contextual_links",
            as: "contextual_links_json",
            type: "string",
          },
          {
            field: "related_articles_to_link_from",
            as: "inbound_links_json",
            type: "string",
          },
          {
            field: "total_suggested_links",
            as: "total_suggested_links",
            type: "number",
          },
          { field: "linking_strategy", as: "linking_strategy", type: "string" },
        ],
      ),

      // 6. Schema markup generator (runCode)
      n("6", "runCode", "Generate Article and FAQ schema markup", 1240, {
        runCodeDetails: {
          language: "javascript",
          code: `// Generate Article + FAQPage combined schema
const article = JSON.parse(vars.seo_article_json || "{}");
const faqs = article.faq_section || [];
const articleSchema = {
  "@context": "https://schema.org",
  "@type": "Article",
  "headline": article.seo_title || article.h1 || "",
  "description": article.meta_description || "",
  "articleBody": (article.article_body || "").replace(/<[^>]+>/g, "").substring(0, 500),
  "wordCount": article.word_count || 0,
  "keywords": (article.secondary_keywords || []).join(", ")
};
const faqSchema = faqs.length > 0 ? {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faqs.map(f => ({
    "@type": "Question",
    "name": f.question,
    "acceptedAnswer": { "@type": "Answer", "text": f.answer }
  }))
} : null;
const schemas = [articleSchema];
if (faqSchema) schemas.push(faqSchema);
const scriptTags = schemas.map(s => '<script type="application/ld+json">' + JSON.stringify(s) + '</script>').join("\\n");
return {
  combined_schemas_json: JSON.stringify(schemas),
  schema_script_tags: scriptTags,
  has_faq_schema: faqSchema ? "true" : "false",
  faq_count: faqs.length,
  article_word_count: article.word_count || 0
};`,
          outputVariables: [
            { name: "combined_schemas_json", type: "string" },
            { name: "schema_script_tags", type: "string" },
            { name: "has_faq_schema", type: "string" },
            { name: "faq_count", type: "number" },
            { name: "article_word_count", type: "number" },
          ],
        },
      }),

      // 7. Article draft delivery (Sheets)
      sheetsNode(
        "7",
        "Log article draft to Sheets",
        1480,
        "Support_Articles",
        `[["${new Date().toISOString()}","{support_question}","{needs_new_article}","{article_word_count}","{faq_count}","{has_faq_schema}","{total_suggested_links}","{competitor_count}","{our_best_position}"]]`,
      ),

      // 8. Team notification (Slack)
      slackNode(
        "8",
        "Slack: Notify team of new support article draft",
        1720,
        "Support-to-SEO Article Ready\\n==============================\\nQuestion: {support_question}\\nNew Article Needed: {needs_new_article}\\nCompetitor Coverage: {competitor_count} competitors\\nOur Current Position: {our_best_position}\\n\\nArticle Stats:\\n- Word Count: {article_word_count}\\n- FAQ Items: {faq_count}\\n- Schema: Article + FAQ ({has_faq_schema})\\n- Internal Links: {total_suggested_links}\\n- Linking Strategy: {linking_strategy}\\n\\nFull article draft, schema, and linking plan in Sheets.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1107. CHANGELOG TO LAUNCH POST (8 nodes)
  // ===================================================================

  "Changelog to Launch Post": {
    summary:
      "Takes a changelog entry, analyzes feature impact with code, generates an AI blog post, AI social posts, and AI email announcement, routes through content approval, publishes across channels via Buffer, and tracks the launch in Sheets.",
    nodes: [
      trigger("Webhook: Changelog entry for launch post", "webhook_app", {
        source: "internal",
        event: "changelog_launch_post",
        howItStarts:
          "A new changelog or release note entry is submitted to automatically generate and distribute a coordinated launch announcement across blog, social, and email channels",
        setup:
          "Configure OpenAI for multi-format content generation. Set up Buffer for social publishing. Provide email template settings. Set up an approval workflow and Sheets for launch tracking.",
        inputFields: [],
      }),

      // 2. Feature impact analyzer (runCode)
      n("2", "runCode", "Analyze changelog entry and feature impact", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Analyze the changelog entry for impact and categorization
const payload = JSON.parse(vars.webhook_payload || "{}");
const title = payload.title || payload.feature_name || "";
const description = payload.description || payload.body || "";
const version = payload.version || "";
const changeType = payload.type || "feature";
// Categorize impact
const impactKeywords = { high: ["major", "breaking", "new product", "platform", "redesign", "launch"], medium: ["improvement", "enhancement", "update", "integration"], low: ["fix", "patch", "minor", "typo", "maintenance"] };
let impact = "medium";
const lowerDesc = (title + " " + description).toLowerCase();
if (impactKeywords.high.some(k => lowerDesc.includes(k))) impact = "high";
else if (impactKeywords.low.some(k => lowerDesc.includes(k))) impact = "low";
const audiences = [];
if (lowerDesc.includes("api") || lowerDesc.includes("sdk") || lowerDesc.includes("developer")) audiences.push("developers");
if (lowerDesc.includes("dashboard") || lowerDesc.includes("ui") || lowerDesc.includes("report")) audiences.push("end_users");
if (lowerDesc.includes("enterprise") || lowerDesc.includes("admin") || lowerDesc.includes("security")) audiences.push("admins");
if (audiences.length === 0) audiences.push("all_users");
return {
  feature_title: title,
  feature_description: description,
  feature_version: version,
  change_type: changeType,
  impact_level: impact,
  target_audiences_json: JSON.stringify(audiences),
  audience_count: audiences.length,
  needs_blog_post: impact !== "low" ? "true" : "false",
  needs_email: impact === "high" ? "true" : "false"
};`,
          outputVariables: [
            { name: "feature_title", type: "string" },
            { name: "feature_description", type: "string" },
            { name: "feature_version", type: "string" },
            { name: "change_type", type: "string" },
            { name: "impact_level", type: "string" },
            { name: "target_audiences_json", type: "string" },
            { name: "audience_count", type: "number" },
            { name: "needs_blog_post", type: "string" },
            { name: "needs_email", type: "string" },
          ],
        },
      }),

      // 3. AI blog post generator
      aiNode(
        "3",
        "AI: Generate launch blog post",
        520,
        "You are a product marketing writer who creates compelling launch blog posts. Write posts that excite users about new features while being clear about the value and how to use them. Include SEO optimization. Return JSON.",
        "Write a launch blog post for: {feature_title}. Description: {feature_description}. Version: {feature_version}. Type: {change_type}. Impact: {impact_level}. Audiences: {target_audiences_json}. Create blog_title (compelling, SEO-friendly), meta_description (120-155 chars), blog_body (full HTML with sections: intro, what's new, how it works, get started CTA), word_count (number), target_keyword (string), og_description (for social sharing). Return blog_post (object with all fields).",
        [{ field: "blog_post", as: "blog_post_json", type: "string" }],
      ),

      // 4. AI social post generator
      aiNode(
        "4",
        "AI: Generate social media launch posts",
        760,
        "You are a social media marketer who creates platform-native launch announcements. Write posts optimized for each platform's format and audience. Return JSON.",
        "Create social posts for feature launch: {feature_title}. Description: {feature_description}. Impact: {impact_level}. Blog post context: {blog_post_json}. Create twitter_post (max 280 chars with emoji), linkedin_post (professional tone, 150-300 words), facebook_post (conversational, with CTA), thread_hook (first tweet of a thread for deep dives). Return social_posts (object with twitter_post, linkedin_post, facebook_post, thread_hook), hashtags (array of 3-5).",
        [
          { field: "social_posts", as: "social_posts_json", type: "string" },
          { field: "hashtags", as: "launch_hashtags_json", type: "string" },
        ],
      ),

      // 5. AI email announcement
      aiNode(
        "5",
        "AI: Generate email announcement",
        1000,
        "You are an email marketing specialist who writes product announcement emails that drive feature adoption. Keep it scannable, benefit-focused, and action-oriented. Return JSON.",
        "Write a product announcement email for: {feature_title}. Description: {feature_description}. Impact: {impact_level}. Audiences: {target_audiences_json}. Create email_subject (compelling, 40-60 chars), preview_text (90 chars), email_body_html (scannable HTML with hero section, key benefits, how-to-start section, CTA button), plain_text_version (string), primary_cta_text (button text), primary_cta_url (placeholder URL). Return announcement_email (object with all fields).",
        [
          {
            field: "announcement_email",
            as: "announcement_email_json",
            type: "string",
          },
        ],
      ),

      // 6. Content approval gate
      n(
        "6",
        "approvalGate",
        "Content team: Approve launch content package",
        1240,
        {
          approvalGateDetails: {
            approvers: ["{content_approver_email}"],
            message:
              "Launch content package for review: {feature_title} ({impact_level} impact, {change_type}).\\n\\nIncludes: Blog post, social posts (Twitter/LinkedIn/Facebook), and email announcement.\\n\\nPlease review all content for accuracy, brand voice, and feature correctness before multi-channel publication.",
            timeout: 24,
          },
        },
      ),

      // 7. Multi-channel publisher (Buffer)
      bufferNode(
        "7",
        "Buffer: Publish social launch posts",
        1480,
        "{social_posts_json}",
      ),

      // 8. Launch tracker (Sheets)
      sheetsNode(
        "8",
        "Log launch to tracker",
        1720,
        "Launch_Tracker",
        `[["${new Date().toISOString()}","{feature_title}","{feature_version}","{change_type}","{impact_level}","{audience_count}","{needs_blog_post}","{needs_email}","published"]]`,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"), // approved -> publish
      e("6", "8", "output_2"), // rejected -> log as rejected
      e("7", "8"),
    ],
  },
};
